import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  HandCoins, Trash2, CircleDollarSign, Clock4, Trophy, ArrowDownCircle, ArrowUpCircle,
  Home, Plus, Paperclip, X, FileDown, Search, RotateCcw, Layers, Pencil, TrendingUp, FileSignature, Calculator, ListChecks, CircleStop,
  ArrowUpNarrowWide, ArrowDownWideNarrow, ChevronDown, Check, Wallet,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminNav } from "../components/admin/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 w-full rounded-md bg-seed-off px-4 text-sm text-seed-deep outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]";

const brl = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const parseNum = (s) => {
  let v = (s ?? "").toString().replace(/[^\d.,]/g, "");
  if (!v) return 0;
  if (v.includes(",")) {
    v = v.replace(/\./g, "").replace(",", ".");
  } else if ((v.match(/\./g) || []).length > 1) {
    v = v.replace(/\./g, "");
  } else if (v.includes(".")) {
    const dec = v.split(".")[1];
    if (dec && dec.length === 3) v = v.replace(".", "");
  }
  return Number(v) || 0;
};
const maskMoney = (v) => {
  let s = (v || "").replace(/[^\d.,]/g, "");
  const i = s.indexOf(",");
  if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/[.,]/g, "").slice(0, 2);
  return s;
};
const maskCommission = (v) => {
  let s = (v || "").replace(/[^\d.,%]/g, "");
  const hasPct = s.includes("%");
  s = maskMoney(s.replace(/%/g, ""));
  return hasPct ? s + "%" : s;
};
const fmtDate = (iso) => (iso ? new Date(iso.length === 10 ? iso + "T12:00:00" : iso).toLocaleDateString("pt-BR") : "—");

const normTxt = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const DupBadge = ({ value, known, testId }) => {
  if (!value || !value.trim()) return null;
  const found = known.some((k) => normTxt(k) === normTxt(value));
  return (
    <p data-testid={testId} className={`mt-1 text-[10px] font-bold ${found ? "text-seed-salad" : "text-amber-600"}`}>
      {found ? "✓ Já cadastrado — será reutilizado" : "+ Novo cadastro"}
    </p>
  );
};

const TagPicker = ({ known, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = known.filter((tg) => !selected.some((s) => normTxt(s) === normTxt(tg)) && normTxt(tg).includes(normTxt(q)));
  const canCreate = q.trim() && !known.some((tg) => normTxt(tg) === normTxt(q)) && !selected.some((s) => normTxt(s) === normTxt(q));
  const similar = canCreate
    ? known.filter((tg) => {
        const a = normTxt(tg);
        const b = normTxt(q);
        return b.length >= 3 && (a.includes(b) || b.includes(a));
      }).slice(0, 3)
    : [];
  const add = (tg) => {
    onChange([...selected, tg]);
    setQ("");
  };
  return (
    <div className="relative">
      <div className={`${inputCls} flex h-auto min-h-12 cursor-text flex-wrap items-center gap-1.5 py-2`} onClick={() => setOpen(true)} data-testid={testId}>
        {selected.map((tg) => (
          <span key={tg} className="flex items-center gap-1 rounded-full bg-seed-salad/15 px-2.5 py-1 text-[10px] font-bold text-seed-deep" data-testid={`${testId}-chip-${normTxt(tg).replace(/\W+/g, "-")}`}>
            {tg}
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(selected.filter((s) => s !== tg)); }} aria-label={`Remover tag ${tg}`} className="text-seed-deep/50 transition-colors hover:text-red-500">
              <X size={11} />
            </button>
          </span>
        ))}
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? "Buscar ou criar tag..." : ""}
          className="min-w-[90px] flex-1 bg-transparent text-sm outline-none placeholder:text-seed-deep/40"
          data-testid={`${testId}-input`}
        />
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 max-h-52 w-full overflow-y-auto rounded-md bg-white p-1.5 shadow-xl" data-testid={`${testId}-dropdown`}>
            {filtered.length === 0 && !canCreate && <p className="px-3 py-2 text-xs text-seed-deep/40">Nenhuma tag encontrada.</p>}
            {filtered.map((tg) => (
              <button type="button" key={tg} onClick={() => add(tg)} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-semibold text-seed-deep transition-colors hover:bg-seed-off" data-testid={`${testId}-opt-${normTxt(tg).replace(/\W+/g, "-")}`}>
                {tg}
              </button>
            ))}
            {canCreate && (
              <>
                <button type="button" onClick={() => add(q.trim())} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-bold text-seed-salad transition-colors hover:bg-seed-off" data-testid={`${testId}-create`}>
                  <Plus size={13} /> Criar tag &#34;{q.trim()}&#34;
                </button>
                {similar.length > 0 && (
                  <p className="px-3 pb-1.5 text-[10px] font-bold leading-snug text-amber-600" data-testid={`${testId}-similar-warning`}>
                    Atenção: já existe tag parecida ({similar.map((s) => `“${s}”`).join(", ")}) — verifique se não é a mesma antes de criar.
                  </p>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const SupplierPicker = ({ known, usage, value, onChange, onRename, onDelete, testId, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const filtered = known.filter((s) => normTxt(s).includes(normTxt(q)));
  const canCreate = q.trim() && !known.some((s) => normTxt(s) === normTxt(q));
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className={`${inputCls} flex items-center justify-between gap-2 text-left`} data-testid={testId}>
        <span className={value ? "" : "text-seed-deep/40"}>{value || placeholder}</span>
        <ChevronDown size={15} className={`shrink-0 text-seed-deep/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setEditing(null); }} />
          <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md bg-white p-1.5 shadow-xl" data-testid={`${testId}-dropdown`}>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar..."
              className="mb-1 h-9 w-full rounded-md bg-seed-off px-3 text-sm text-seed-deep outline-none placeholder:text-seed-deep/40"
              data-testid={`${testId}-search`}
            />
            {filtered.length === 0 && !canCreate && <p className="px-3 py-2 text-xs text-seed-deep/40">Nenhum cadastro encontrado.</p>}
            {filtered.map((s) => {
              const used = usage[s] || 0;
              const rowKey = normTxt(s).replace(/\W+/g, "-").slice(0, 24);
              if (editing === s) {
                return (
                  <div key={s} className="flex items-center gap-1.5 px-2 py-1.5">
                    <input autoFocus value={editVal} onChange={(e) => setEditVal(e.target.value)} className="h-8 flex-1 rounded-md bg-seed-off px-2 text-xs font-semibold text-seed-deep outline-none" data-testid={`${testId}-rename-input`} />
                    <button
                      type="button"
                      onClick={async () => {
                        const to = editVal.trim();
                        if (to && to !== s) {
                          const ok = await onRename(s, to);
                          if (ok && value === s) onChange(to);
                        }
                        setEditing(null);
                      }}
                      className="rounded-full bg-seed-deep px-3 py-1.5 text-[10px] font-bold text-seed-off"
                      data-testid={`${testId}-rename-save`}
                    >
                      OK
                    </button>
                    <button type="button" onClick={() => setEditing(null)} className="text-[10px] font-bold text-seed-deep/50">X</button>
                  </div>
                );
              }
              return (
                <div key={s} className="group flex items-center gap-1 rounded px-1 hover:bg-seed-off">
                  <button type="button" onClick={() => { onChange(s); setOpen(false); setQ(""); }} className="flex flex-1 items-center gap-2 px-2 py-2 text-left text-sm font-semibold text-seed-deep" data-testid={`${testId}-opt-${rowKey}`}>
                    {value === s && <Check size={13} className="text-seed-salad" />}
                    {s}
                  </button>
                  <button type="button" onClick={() => { setEditing(s); setEditVal(s); }} aria-label={`Renomear ${s}`} title="Renomear" className="p-1.5 text-seed-deep/30 transition-colors hover:text-seed-salad" data-testid={`${testId}-edit-${rowKey}`}>
                    <Pencil size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => used === 0 && onDelete(s)}
                    disabled={used > 0}
                    aria-label={`Excluir ${s}`}
                    title={used > 0 ? `Em uso por ${used} lançamento(s)` : "Excluir cadastro"}
                    className={`p-1.5 transition-colors ${used > 0 ? "cursor-not-allowed text-seed-deep/15" : "text-seed-deep/30 hover:text-red-500"}`}
                    data-testid={`${testId}-delete-${rowKey}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              );
            })}
            {canCreate && (
              <button type="button" onClick={() => { onChange(q.trim()); setOpen(false); setQ(""); }} className="mt-0.5 flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-bold text-seed-salad transition-colors hover:bg-seed-off" data-testid={`${testId}-create`}>
                <Plus size={13} /> Criar &#34;{q.trim()}&#34;
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const CategoryPicker = ({ options, value, onChange, onCreate, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const sorted = useMemo(() => [...options].sort((a, b) => a.localeCompare(b, "pt-BR")), [options]);
  const filtered = sorted.filter((c) => normTxt(c).includes(normTxt(q)));
  const canCreate = q.trim() && !sorted.some((c) => normTxt(c) === normTxt(q));
  const pick = (c) => { onChange(c); setOpen(false); setQ(""); };
  const create = async () => {
    const name = q.trim();
    if (!name || saving) return;
    setSaving(true);
    const ok = await onCreate(name);
    setSaving(false);
    if (ok) { setOpen(false); setQ(""); }
  };
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className={`${inputCls} flex items-center justify-between gap-2 text-left`} data-testid={testId}>
        <span className={`truncate ${value ? "" : "text-seed-deep/40"}`}>{value || "Categoria..."}</span>
        <ChevronDown size={15} className={`shrink-0 text-seed-deep/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 max-h-64 w-full min-w-56 overflow-y-auto rounded-md bg-white p-1.5 shadow-xl" data-testid={`${testId}-dropdown`}>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (canCreate) create();
                else if (filtered.length >= 1) pick(filtered[0]);
              }}
              placeholder="Buscar ou criar categoria..."
              className="mb-1 h-9 w-full rounded-md bg-seed-off px-3 text-sm text-seed-deep outline-none placeholder:text-seed-deep/40"
              data-testid={`${testId}-search`}
            />
            {filtered.length === 0 && !canCreate && <p className="px-3 py-2 text-xs text-seed-deep/40">Nenhuma categoria encontrada.</p>}
            {filtered.map((c) => (
              <button key={c} type="button" onClick={() => pick(c)} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-semibold text-seed-deep transition-colors hover:bg-seed-off" data-testid={`${testId}-opt-${normTxt(c).replace(/\W+/g, "-").slice(0, 24)}`}>
                {value === c && <Check size={13} className="shrink-0 text-seed-salad" />}
                <span className="truncate">{c}</span>
              </button>
            ))}
            {canCreate && (
              <button type="button" onClick={create} disabled={saving} className="mt-0.5 flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-bold text-seed-salad transition-colors hover:bg-seed-off disabled:opacity-60" data-testid={`${testId}-create`}>
                <Plus size={13} className="shrink-0" /> {saving ? "Criando..." : `Criar "${q.trim()}"`}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};



const AccountPicker = ({ options, value, onChange, onCreate, onRename, onDelete, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const sorted = useMemo(() => [...options].sort((a, b) => a.localeCompare(b, "pt-BR")), [options]);
  const filtered = sorted.filter((c) => normTxt(c).includes(normTxt(q)));
  const canCreate = q.trim() && !sorted.some((c) => normTxt(c) === normTxt(q));
  const close = () => { setOpen(false); setQ(""); setEditing(null); };
  const pick = (c) => { onChange(c); close(); };
  const create = async () => {
    const name = q.trim();
    if (!name || saving) return;
    setSaving(true);
    const ok = await onCreate(name);
    setSaving(false);
    if (ok) close();
  };
  const saveRename = async () => {
    const to = editVal.trim();
    if (!to || !editing) return;
    if (normTxt(to) === normTxt(editing)) { setEditing(null); return; }
    setSaving(true);
    const ok = await onRename(editing, to);
    setSaving(false);
    if (ok) setEditing(null);
  };
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className={`${inputCls} flex items-center justify-between gap-2 text-left`} data-testid={testId}>
        <span className={`truncate ${value ? "" : "text-seed-deep/40"}`}>{value || "Conta (opcional)..."}</span>
        <ChevronDown size={15} className={`shrink-0 text-seed-deep/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={close} />
          <div className="absolute z-50 mt-1 max-h-64 w-full min-w-56 overflow-y-auto rounded-md bg-white p-1.5 shadow-xl" data-testid={`${testId}-dropdown`}>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (canCreate) create();
                else if (filtered.length >= 1) pick(filtered[0]);
              }}
              placeholder="Buscar ou criar conta..."
              className="mb-1 h-9 w-full rounded-md bg-seed-off px-3 text-sm text-seed-deep outline-none placeholder:text-seed-deep/40"
              data-testid={`${testId}-search`}
            />
            {value && (
              <button type="button" onClick={() => pick("")} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs font-semibold text-seed-deep/40 transition-colors hover:bg-seed-off" data-testid={`${testId}-clear`}>
                Limpar seleção (sem conta)
              </button>
            )}
            {filtered.length === 0 && !canCreate && <p className="px-3 py-2 text-xs text-seed-deep/40">Nenhuma conta encontrada.</p>}
            {filtered.map((c) => {
              const rowKey = normTxt(c).replace(/\W+/g, "-").slice(0, 24);
              return editing === c ? (
                <div key={c} className="flex items-center gap-1.5 rounded bg-seed-off/60 px-2 py-1.5">
                  <input
                    autoFocus
                    value={editVal}
                    onChange={(e) => setEditVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveRename(); } if (e.key === "Escape") setEditing(null); }}
                    className="h-8 w-full rounded bg-white px-2 text-xs font-bold text-seed-deep outline-none"
                    data-testid={`${testId}-rename-input`}
                  />
                  <button type="button" onClick={saveRename} disabled={saving} aria-label="Salvar novo nome" className="shrink-0 text-seed-deep/40 hover:text-seed-salad" data-testid={`${testId}-rename-save`}>
                    <Check size={13} />
                  </button>
                  <button type="button" onClick={() => setEditing(null)} aria-label="Cancelar" className="shrink-0 text-seed-deep/40 hover:text-red-500">
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div key={c} className="group flex items-center gap-1 rounded px-3 py-2 transition-colors hover:bg-seed-off" data-testid={`${testId}-opt-${rowKey}`}>
                  <button type="button" onClick={() => pick(c)} className="flex flex-1 items-center gap-2 text-left text-sm font-semibold text-seed-deep">
                    {value === c && <Check size={13} className="shrink-0 text-seed-salad" />}
                    <span className="truncate">{c}</span>
                  </button>
                  <button type="button" onClick={() => { setEditing(c); setEditVal(c); }} aria-label={`Renomear ${c}`} title="Renomear (lançamentos são atualizados)" className="shrink-0 text-seed-deep/45 transition-colors hover:text-seed-salad" data-testid={`${testId}-rename-${rowKey}`}>
                    <Pencil size={12} />
                  </button>
                  <button type="button" onClick={() => onDelete(c)} aria-label={`Excluir ${c}`} title="Excluir (só se não houver lançamentos)" className="shrink-0 text-seed-deep/45 transition-colors hover:text-red-500" data-testid={`${testId}-delete-${rowKey}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              );
            })}
            {canCreate && (
              <button type="button" onClick={create} disabled={saving} className="mt-0.5 flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-bold text-seed-salad transition-colors hover:bg-seed-off disabled:opacity-60" data-testid={`${testId}-create`}>
                <Plus size={13} className="shrink-0" /> {saving ? "Criando..." : `Criar conta "${q.trim()}"`}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const CadastroPanel = ({ title, items, renderChips, editName, input, saving, onEdit, onChange, onCancel, onSave, onDelete, testId, open, onToggle }) => (
  <div className="rounded-lg bg-white p-5 shadow-sm" data-testid={testId}>
    <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-2 text-left" data-testid={`${testId}-toggle`}>
      <span className="flex items-center gap-2">
        <ChevronDown size={15} className={`text-seed-deep/40 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        <h3 className="font-heading text-base font-bold text-seed-deep">{title}</h3>
      </span>
      <span className="text-[10px] font-bold text-seed-deep/35">{items.length} {items.length === 1 ? "item" : "itens"}</span>
    </button>
    {open && (
      <>
        <p className="mt-1 text-[11px] text-seed-deep/45">Toque na pílula para renomear (salvar com nome existente mescla). Lixeira exclui se não estiver em uso.</p>
        {items.length === 0 ? (
          <p className="mt-4 text-xs text-seed-deep/40">Nenhum cadastro ainda — aparecem aqui conforme você lança contas.</p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {items.map((it) => {
              const rowKey = normTxt(it.name).replace(/\W+/g, "-").slice(0, 24);
              const isEditing = editName === it.name;
              return isEditing ? (
                <div key={it.name} className="flex items-center gap-1 rounded-full border border-seed-salad/50 bg-white px-1.5 py-1" data-testid={`${testId}-row-${rowKey}`}>
                  <input
                    value={input}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSave(); } if (e.key === "Escape") onCancel(); }}
                    className="h-7 w-36 rounded-full bg-seed-off px-2.5 text-xs font-bold text-seed-deep outline-none"
                    data-testid={`${testId}-input`}
                    autoFocus
                  />
                  <button onClick={onSave} disabled={saving} data-testid={`${testId}-save`} aria-label="Salvar" className="text-seed-deep/40 transition-colors hover:text-seed-salad disabled:opacity-50">
                    <Check size={13} />
                  </button>
                  <button onClick={onCancel} data-testid={`${testId}-cancel`} aria-label="Cancelar" className="text-seed-deep/40 transition-colors hover:text-red-500">
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div key={it.name} className="flex items-center gap-1 rounded-full border border-seed-deep/10 bg-seed-off/50 py-1 pl-3 pr-1.5 transition-colors hover:border-seed-salad/40" data-testid={`${testId}-row-${rowKey}`}>
                  <button type="button" onClick={() => onEdit(it.name)} title="Toque para renomear" className="text-xs font-bold text-seed-deep transition-colors hover:text-seed-salad" data-testid={`${testId}-edit-${rowKey}`}>
                    {it.name}
                  </button>
                  <span className="text-[9px] font-bold text-seed-deep/35">×{it.count}</span>
                  {renderChips && renderChips(it)}
                  <button
                    onClick={() => onDelete(it.name)}
                    disabled={it.count > 0}
                    title={it.count > 0 ? `Em uso por ${it.count} lançamento(s) — renomeie/mescle antes` : "Excluir cadastro"}
                    aria-label={`Excluir ${it.name}`}
                    data-testid={`${testId}-delete-${rowKey}`}
                    className={`transition-colors ${it.count > 0 ? "cursor-not-allowed text-seed-deep/10" : "text-seed-deep/30 hover:text-red-500"}`}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </>
    )}
  </div>
);

const CATS = {
  pagar: ["Reforma", "Impostos", "Escritura/Cartório", "Documentação", "Manutenção", "Marketing", "Outros"],
  receber: ["Comissão", "Aluguel", "Venda", "Outros"],
};

const DONUT_COLORS = ["#004C3F", "#00D765", "#1AA886", "#C9A227", "#2F855A", "#8B5CF6", "#E11D48", "#0EA5E9"];

const SUBTABS = [
  { key: "dashboard", label: "Visão geral", testId: "fin-sub-dashboard" },
  { key: "contas", label: "Lançamentos", testId: "fin-sub-contas" },
  { key: "calendario", label: "Calendário", testId: "fin-sub-calendario" },
  { key: "relatorios", label: "Relatórios", testId: "fin-sub-relatorios" },
  { key: "cadastros", label: "Cadastros", testId: "fin-sub-cadastros" },
];

const REPORT_TABS = [
  { key: "comissoes", label: "Comissões", testId: "fin-sub-comissoes" },
  { key: "anual", label: "Resultado anual", testId: "fin-sub-anual" },
  { key: "previsao", label: "Previsão", testId: "fin-sub-previsao" },
  { key: "centros", label: "Centros de custo", testId: "fin-sub-centros" },
  { key: "custos", label: "Custos por imóvel", testId: "fin-sub-custos" },
];

const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const monthEnd = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
};

const emptyTx = {
  kind: "pagar", category: "Reforma", description: "", amount: "", due_date: "",
  supplier: "", paid_date: "", notes: "", tagsText: "", attachments: [], property_id: "", recurring: false, frequency: "mensal", recurrenceMonths: "",
  parcelas: "1", costCenter: "", account: "",
};

export default function AdminFinanceiro() {
  const { user, headers, logout } = useAdminAuth();
  const [subtab, setSubtab] = useState("dashboard");
  const [reportTab, setReportTab] = useState("comissoes");
  const [showTxForm, setShowTxForm] = useState(false);
  const [showMoreTx, setShowMoreTx] = useState(false);
  const [cadSearch, setCadSearch] = useState("");
  const [openCads, setOpenCads] = useState({});
  const cadFilterItems = (items) => {
    const qs = normTxt(cadSearch);
    return qs ? items.filter((it) => normTxt(it.name).includes(qs)) : items;
  };
  const cadIsOpen = (key, items) => Boolean(openCads[key]) || (cadSearch.trim() !== "" && cadFilterItems(items).length > 0);
  const cadIsHidden = (items) => cadSearch.trim() !== "" && cadFilterItems(items).length === 0;
  const toggleCad = (key) => setOpenCads((m) => ({ ...m, [key]: !m[key] }));
  const effSubtab = subtab === "relatorios" ? reportTab : subtab;
  const [deals, setDeals] = useState(null);
  const [txs, setTxs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ lead_id: "", property_id: "", sale_value: "", commission: "", broker_email: "" });
  const [simSale, setSimSale] = useState("");
  const [simCommission, setSimCommission] = useState("6%");
  const [simBrokerPct, setSimBrokerPct] = useState("50");
  const [simBroker, setSimBroker] = useState("");
  const [txForm, setTxForm] = useState({ ...emptyTx });
  const [costPropId, setCostPropId] = useState("");
  const [saving, setSaving] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [fFrom, setFFrom] = useState(monthStart());
  const [fTo, setFTo] = useState(monthEnd());
  const [fTag, setFTag] = useState("");
  const [applied, setApplied] = useState({ from: monthStart(), to: monthEnd(), tag: "" });
  const [calMonth, setCalMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [calFrom, setCalFrom] = useState("");
  const [calTo, setCalTo] = useState("");
  const [openDay, setOpenDay] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);
  const [anualYear, setAnualYear] = useState(() => new Date().getFullYear());
  const [exFrom, setExFrom] = useState(monthStart());
  const [exTo, setExTo] = useState(monthEnd());
  const [exporting, setExporting] = useState(false);
  const [showExtrato, setShowExtrato] = useState(false);
  const [exStatus, setExStatus] = useState("todos");
  const [exCat, setExCat] = useState("todas");
  const [exKind, setExKind] = useState("todos");
  const [exSupplier, setExSupplier] = useState("todos");
  const [exCenter, setExCenter] = useState("todos");
  const [cadEditing, setCadEditing] = useState(null);
  const [cadInput, setCadInput] = useState("");
  const [cadSaving, setCadSaving] = useState(false);
  const [groupEdit, setGroupEdit] = useState(null);
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupForm, setGroupForm] = useState({ amount: "", category: "", supplier: "", costCenter: "", account: "", tagsText: "", notes: "", nextDue: "", includeSettled: false });
  const [editingId, setEditingId] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [budgetInputs, setBudgetInputs] = useState({});
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [signerNames, setSignerNames] = useState({});
  const [contractUploading, setContractUploading] = useState(false);
  const [csRows, setCsRows] = useState({});
  const [csSending, setCsSending] = useState(false);

  const apiError = (err, fallback) => {
    const d = err.response?.data?.detail;
    return typeof d === "string" ? d : d ? JSON.stringify(d) : fallback;
  };
  const [selected, setSelected] = useState([]);
  const [settings, setSettings] = useState(null);
  const [saldoInput, setSaldoInput] = useState("");
  const [meta, setMeta] = useState(null);

  const cats = meta?.categories?.pagar?.length ? meta.categories : CATS;
  const accounts = meta?.accounts || [];

  const knownSuppliers = useMemo(
    () => [...new Set(txs.filter((t) => t.kind === txForm.kind).map((t) => t.supplier).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [txs, txForm.kind]
  );
  const knownCenters = useMemo(
    () => [...new Set([...txs.map((t) => t.cost_center).filter(Boolean), ...budgets.map((b) => b.cost_center).filter(Boolean)])].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [txs, budgets]
  );
  const knownTags = useMemo(
    () => [...new Set(txs.flatMap((t) => t.tags || []))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [txs]
  );
  const extratoTxs = useMemo(
    () => txs
      .filter((t) => t.due_date && t.due_date >= exFrom && t.due_date <= exTo)
      .filter((t) => exStatus === "todos" || (exStatus === "quitado" ? t.status === "quitado" : t.status !== "quitado"))
      .filter((t) => exCat === "todas" || t.category === exCat)
      .filter((t) => exKind === "todos" || t.kind === exKind)
      .filter((t) => exSupplier === "todos" || t.supplier === exSupplier)
      .filter((t) => exCenter === "todos" || t.cost_center === exCenter)
      .sort((a, b) => a.due_date.localeCompare(b.due_date)),
    [txs, exFrom, exTo, exStatus, exCat, exKind, exSupplier, exCenter]
  );
  const extratoSuppliers = useMemo(
    () => [...new Set(txs.map((t) => t.supplier).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [txs]
  );
  const extratoCats = useMemo(
    () => [...new Set(txs.map((t) => t.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [txs]
  );
  const extratoEntradas = extratoTxs.filter((t) => t.kind === "receber").reduce((s, t) => s + (t.amount_value || 0), 0);
  const extratoSaidas = extratoTxs.filter((t) => t.kind === "pagar").reduce((s, t) => s + (t.amount_value || 0), 0);

  const brokers = useMemo(() => (users || []).filter((x) => x.role === "corretor"), [users]);
  const simBrokerName = brokers.find((b) => b.email === simBroker)?.name?.split(" ")[0];
  const simSaleValue = parseNum(simSale);
  const simTotal = simCommission.trim().endsWith("%") ? simSaleValue * (parseNum(simCommission.replace("%", "")) / 100) : parseNum(simCommission);
  const simPct = Math.min(100, Math.max(0, parseNum(simBrokerPct)));
  const simBrokerValue = simTotal * (simPct / 100);
  const simAgencyValue = simTotal - simBrokerValue;

  const suppliersList = useMemo(() => {
    const monthPrefix = new Date().toISOString().slice(0, 7);
    const map = new Map();
    txs.forEach((t) => {
      if (!t.supplier) return;
      const cur = map.get(t.supplier) || { name: t.supplier, count: 0, kinds: new Set(), monthPagar: 0, monthReceber: 0 };
      cur.count += 1;
      cur.kinds.add(t.kind);
      if ((t.due_date || "").startsWith(monthPrefix)) {
        if (t.kind === "pagar") cur.monthPagar += t.amount_value || 0;
        else cur.monthReceber += t.amount_value || 0;
      }
      map.set(t.supplier, cur);
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [txs]);

  const centersList = useMemo(() => {
    const map = new Map();
    txs.forEach((t) => {
      if (!t.cost_center) return;
      const cur = map.get(t.cost_center) || { name: t.cost_center, count: 0 };
      cur.count += 1;
      map.set(t.cost_center, cur);
    });
    budgets.forEach((b) => {
      if (!b.cost_center) return;
      const cur = map.get(b.cost_center) || { name: b.cost_center, count: 0 };
      cur.budget = b.amount;
      map.set(b.cost_center, cur);
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [txs, budgets]);

  const tagsList = useMemo(() => {
    const map = new Map();
    txs.forEach((t) => {
      (t.tags || []).forEach((tg) => {
        const cur = map.get(tg) || { name: tg, count: 0 };
        cur.count += 1;
        map.set(tg, cur);
      });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [txs]);

  const categoriesList = useMemo(() => {
    const map = new Map();
    const add = (name, kind) => {
      const cur = map.get(name) || { name, count: 0, kinds: new Set() };
      cur.kinds.add(kind);
      map.set(name, cur);
    };
    (cats.pagar || []).forEach((c) => add(c, "pagar"));
    (cats.receber || []).forEach((c) => add(c, "receber"));
    txs.forEach((t) => {
      const cur = map.get(t.category);
      if (cur) cur.count += 1;
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [txs, cats]);

  const stopRecurrence = async (t) => {
    if (!window.confirm(`Encerrar a recorrência de "${t.description}"? Os lançamentos já gerados permanecem; os próximos não serão mais criados.`)) return;
    try {
      await axios.post(`${API}/financeiro/transactions/${t.id}/stop-recurrence`, {}, { headers: headers() });
      toast.success("Recorrência encerrada — os próximos não serão mais gerados.");
      await load();
    } catch (err) {
      toast.error(apiError(err, "Não foi possível encerrar a recorrência."));
    }
  };

  const openGroupEdit = (t, series = false) => {
    const key = series ? (t.recurring ? t.id : t.recurrence_source_id) : t.installment_group;
    setGroupEdit({ tx: t, series, key });
    setGroupForm({ amount: "", category: "", supplier: "", costCenter: "", account: "", tagsText: "", notes: "", nextDue: "", includeSettled: false });
  };

  const saveGroupEdit = async () => {
    const payload = { include_settled: groupForm.includeSettled };
    if (groupForm.amount.trim()) payload.amount = groupForm.amount;
    if (groupForm.category) payload.category = groupForm.category;
    if (groupForm.supplier.trim()) payload.supplier = groupForm.supplier;
    if (groupForm.costCenter.trim()) payload.cost_center = groupForm.costCenter;
    if (groupForm.account) payload.account = groupForm.account;
    if (groupForm.tagsText.trim()) payload.tags = groupForm.tagsText.split(",").map((s) => s.trim()).filter(Boolean);
    if (groupForm.notes.trim()) payload.notes = groupForm.notes;
    if (groupForm.nextDue) payload.next_due_date = groupForm.nextDue;
    if (Object.keys(payload).length === 1) {
      toast.error("Preencha ao menos um campo para alterar.");
      return;
    }
    setGroupSaving(true);
    try {
      const r = await axios.put(`${API}/financeiro/transactions/group/${groupEdit.key}${groupEdit.series ? "?series=true" : ""}`, payload, { headers: headers() });
      toast.success(`${r.data.updated} parcela(s) atualizada(s)${r.data.redistributed ? ` — datas redistribuídas em ${r.data.redistributed} parcela(s)` : ""}.`);
      setGroupEdit(null);
      await load();
    } catch (err) {
      toast.error(apiError(err, "Não foi possível editar a sequência."));
    } finally {
      setGroupSaving(false);
    }
  };

  const supplierUsage = useMemo(() => {
    const map = {};
    txs.forEach((t) => {
      if (t.supplier) map[t.supplier] = (map[t.supplier] || 0) + 1;
    });
    return map;
  }, [txs]);

  const quickRenameSupplier = async (from, to) => {
    try {
      await axios.post(`${API}/financeiro/cadastros/rename`, { field: "supplier", from_name: from, to_name: to }, { headers: headers() });
      toast.success(`"${from}" renomeado para "${to}".`);
      await load();
      return true;
    } catch (err) {
      toast.error(apiError(err, "Não foi possível renomear."));
      return false;
    }
  };

  const quickDeleteSupplier = async (name) => {
    if (!window.confirm(`Excluir o cadastro "${name}"?`)) return;
    try {
      await axios.delete(`${API}/financeiro/cadastros/supplier/${encodeURIComponent(name)}`, { headers: headers() });
      toast.success("Cadastro excluído.");
      if (txForm.supplier === name) setTxForm((f) => ({ ...f, supplier: "" }));
      await load();
    } catch (err) {
      toast.error(apiError(err, "Não foi possível excluir."));
    }
  };

  const deleteCadastro = async (field, name) => {
    if (!window.confirm(`Excluir o cadastro "${name}"? Só é possível excluir cadastros sem lançamentos.`)) return;
    try {
      await axios.delete(`${API}/financeiro/cadastros/${field}/${encodeURIComponent(name)}`, { headers: headers() });
      toast.success("Cadastro excluído.");
      await load();
    } catch (err) {
      toast.error(apiError(err, "Não foi possível excluir."));
    }
  };

  const saveCadastro = async (field) => {
    const from = cadEditing?.name;
    const to = cadInput.trim();
    if (!from || !to || to === from) {
      setCadEditing(null);
      return;
    }
    const known = field === "supplier" ? suppliersList : field === "tags" ? tagsList : field === "category" ? categoriesList : centersList;
    const existing = known.find((k) => normTxt(k.name) === normTxt(to));
    if (existing && !window.confirm(`"${to}" já existe. Mesclar "${from}" em "${existing.name}"? Todos os lançamentos serão unificados.`)) return;
    setCadSaving(true);
    try {
      const r = await axios.post(`${API}/financeiro/cadastros/rename`, { field, from_name: from, to_name: to }, { headers: headers() });
      toast.success(existing ? `Cadastros mesclados — ${r.data.updated} lançamento(s) atualizados.` : `Renomeado — ${r.data.updated} lançamento(s) atualizados.`);
      setCadEditing(null);
      await load();
    } catch (err) {
      toast.error(apiError(err, "Não foi possível renomear."));
    } finally {
      setCadSaving(false);
    }
  };

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const [d, l, p, u, t, b, s, m] = await Promise.all([
        axios.get(`${API}/crm/deals`, { headers: headers() }),
        axios.get(`${API}/crm/leads`, { headers: headers() }),
        axios.get(`${API}/properties`),
        axios.get(`${API}/users`, { headers: headers() }),
        axios.get(`${API}/financeiro/transactions`, { headers: headers() }),
        axios.get(`${API}/financeiro/budgets`, { headers: headers() }),
        axios.get(`${API}/financeiro/settings`, { headers: headers() }),
        axios.get(`${API}/financeiro/meta`, { headers: headers() }),
      ]);
      setDeals(d.data);
      setLeads(l.data);
      setProperties(p.data);
      setUsers(u.data.filter((x) => x.active !== false));
      setTxs(t.data);
      setBudgets(b.data);
      setSettings(s.data);
      setSaldoInput(s.data.initial_balance || "");
      setMeta(m.data);
    } catch {
      setDeals([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setTx = (k) => (e) => {
    const v = e.target.value;
    setTxForm((f) => ({ ...f, [k]: v, ...(k === "kind" ? { category: (cats[v] || CATS[v])[0] } : {}) }));
  };

  const createCategory = async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return false;
    try {
      await axios.post(`${API}/financeiro/categories`, { kind: txForm.kind, name: trimmed }, { headers: headers() });
      setMeta((m) => ({
        ...(m || { categories: CATS, accounts: [] }),
        categories: {
          ...(m?.categories || CATS),
          [txForm.kind]: [...new Set([...(m?.categories?.[txForm.kind] || CATS[txForm.kind]), trimmed])],
        },
      }));
      setTxForm((f) => ({ ...f, category: trimmed }));
      toast.success(`Categoria "${trimmed}" criada!`);
      return true;
    } catch {
      toast.error("Não foi possível criar a categoria.");
      return false;
    }
  };

  const createAccount = async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return false;
    try {
      await axios.post(`${API}/financeiro/accounts`, { name: trimmed }, { headers: headers() });
      setMeta((m) => ({ ...(m || { categories: CATS, accounts: [] }), accounts: [...new Set([...(m?.accounts || []), trimmed])] }));
      setTxForm((f) => ({ ...f, account: trimmed }));
      toast.success(`Conta "${trimmed}" criada!`);
      return true;
    } catch {
      toast.error("Não foi possível criar a conta.");
      return false;
    }
  };

  const renameAccount = async (fromName, toName) => {
    try {
      await axios.post(`${API}/financeiro/cadastros/rename`, { field: "account", from_name: fromName, to_name: toName.trim() }, { headers: headers() });
      setMeta((m) => ({ ...m, accounts: (m?.accounts || []).map((a) => (a === fromName ? toName.trim() : a)) }));
      setTxForm((f) => (f.account === fromName ? { ...f, account: toName.trim() } : f));
      toast.success(`Conta renomeada para "${toName.trim()}".`);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível renomear.");
      return false;
    }
  };

  const deleteAccount = async (name) => {
    if (!window.confirm(`Excluir a conta "${name}"? Só é possível se não houver lançamentos nela.`)) return;
    try {
      await axios.delete(`${API}/financeiro/cadastros/account/${encodeURIComponent(name)}`, { headers: headers() });
      setMeta((m) => ({ ...m, accounts: (m?.accounts || []).filter((a) => a !== name) }));
      setTxForm((f) => (f.account === name ? { ...f, account: "" } : f));
      toast.success("Conta excluída.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível excluir.");
    }
  };

  const leadName = (id) => leads.find((l) => l.id === id)?.nome || "Contato";
  const propTitle = (id) => properties.find((p) => p.id === id)?.title || null;
  const userName = (email) => users.find((u) => u.email === email)?.name || email;

  const createDeal = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/crm/deals`,
        { lead_id: form.lead_id, property_id: form.property_id || null, sale_value: form.sale_value, commission: form.commission, broker_email: form.broker_email || null },
        { headers: headers() }
      );
      toast.success("Negócio registrado e contato marcado como ganho!");
      setForm({ lead_id: "", property_id: "", sale_value: "", commission: "", broker_email: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível registrar.");
    } finally {
      setSaving(false);
    }
  };

  const toggleDeal = async (deal) => {
    try {
      const status = deal.status === "recebida" ? "a_receber" : "recebida";
      await axios.put(`${API}/crm/deals/${deal.id}`, { status }, { headers: headers() });
      setDeals((ds) => ds.map((d) => (d.id === deal.id ? { ...d, status } : d)));
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  const removeDeal = async (id) => {
    if (!window.confirm("Excluir este registro?")) return;
    await axios.delete(`${API}/crm/deals/${id}`, { headers: headers() });
    setDeals((ds) => ds.filter((d) => d.id !== id));
  };

  const attachFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setAttaching(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await axios.post(`${API}/upload`, fd, { headers: headers() });
        urls.push(r.data.url);
      }
      setTxForm((f) => ({ ...f, attachments: [...f.attachments, ...urls] }));
      toast.success(`${urls.length} anexo(s) enviado(s)!`);
    } catch {
      toast.error("Falha no envio do anexo.");
    } finally {
      setAttaching(false);
    }
  };

  const openTxEdit = (t) => {
    setEditingId(t.id);
    setTxForm({
      kind: t.kind,
      category: t.category,
      description: t.description,
      amount: (t.amount_value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      due_date: t.due_date || "",
      supplier: t.supplier || "",
      paid_date: t.paid_date || "",
      notes: t.notes || "",
      tagsText: (t.tags || []).join(", "),
      attachments: t.attachments || [],
      property_id: t.property_id || "",
      recurring: !!t.recurring,
      frequency: t.frequency || "mensal",
      recurrenceMonths: t.recurrence_months ? String(t.recurrence_months) : "",
      parcelas: "1",
      costCenter: t.cost_center || "",
      account: t.account || "",
    });
    setShowTxForm(true);
    setShowMoreTx(true);
    setTimeout(() => document.querySelector('[data-testid="tx-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  const cancelTxEdit = () => {
    setEditingId(null);
    setTxForm({ ...emptyTx });
    setShowTxForm(false);
    setShowMoreTx(false);
  };

  const saveTx = async (e, andAnother = false) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(
          `${API}/financeiro/transactions/${editingId}`,
          {
            category: txForm.category,
            description: txForm.description,
            amount: txForm.amount,
            due_date: txForm.due_date || null,
            supplier: txForm.supplier || null,
            paid_date: txForm.paid_date || null,
            notes: txForm.notes || null,
            tags: txForm.tagsText.split(",").map((s) => s.trim()).filter(Boolean),
            attachments: txForm.attachments,
            cost_center: txForm.costCenter || null,
            account: txForm.account || null,
            ...(txForm.recurring ? { recurrence_months: Number(txForm.recurrenceMonths) || 0 } : {}),
          },
          { headers: headers() }
        );
        toast.success("Lançamento atualizado!");
        cancelTxEdit();
      } else {
        const r = await axios.post(
          `${API}/financeiro/transactions`,
          {
            kind: txForm.kind,
            category: txForm.category,
            description: txForm.description,
            amount: txForm.amount,
            due_date: txForm.due_date || null,
            property_id: txForm.property_id || null,
            supplier: txForm.supplier || null,
            paid_date: txForm.paid_date || null,
            notes: txForm.notes || null,
            tags: txForm.tagsText.split(",").map((s) => s.trim()).filter(Boolean),
            attachments: txForm.attachments,
            recurring: txForm.recurring,
            frequency: txForm.frequency,
            ...(txForm.recurring && txForm.recurrenceMonths ? { recurrence_months: Number(txForm.recurrenceMonths) } : {}),
            installments: Number(txForm.parcelas) || 1,
            cost_center: txForm.costCenter || null,
            account: txForm.account || null,
          },
          { headers: headers() }
        );
        toast.success(r.data.created > 1 ? `${r.data.created} parcelas geradas, cada uma com seu vencimento!` : "Lançamento registrado!");
        if (andAnother) {
          setTxForm({ ...emptyTx, kind: txForm.kind, category: txForm.category });
        } else {
          setTxForm({ ...emptyTx, kind: txForm.kind, category: CATS[txForm.kind][0] });
          setShowTxForm(false);
          setShowMoreTx(false);
        }
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const toggleTx = async (t) => {
    try {
      if (t.status === "quitado") {
        await axios.put(`${API}/financeiro/transactions/${t.id}`, { status: "pendente" }, { headers: headers() });
        setTxs((xs) => xs.map((x) => (x.id === t.id ? { ...x, status: "pendente" } : x)));
      } else {
        const today = new Date().toISOString().slice(0, 10);
        await axios.put(`${API}/financeiro/transactions/${t.id}`, { status: "quitado", paid_date: today }, { headers: headers() });
        setTxs((xs) => xs.map((x) => (x.id === t.id ? { ...x, status: "quitado", paid_date: today } : x)));
      }
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  const removeTx = async (id) => {
    if (!window.confirm("Excluir este lançamento?")) return;
    await axios.delete(`${API}/financeiro/transactions/${id}`, { headers: headers() });
    setTxs((xs) => xs.filter((x) => x.id !== id));
  };

  const removeGroup = async (t) => {
    const count = txs.filter((x) => x.installment_group === t.installment_group).length;
    if (!window.confirm(`Excluir todas as ${count} parcelas de "${t.description.replace(/\s\(\d+\/\d+\)$/, "")}"?`)) return;
    try {
      await axios.delete(`${API}/financeiro/transactions/group/${t.installment_group}`, { headers: headers() });
      setTxs((xs) => xs.filter((x) => x.installment_group !== t.installment_group));
      toast.success(`${count} parcelas excluídas.`);
    } catch {
      toast.error("Não foi possível excluir o grupo de parcelas.");
    }
  };

  const saveBudget = async (center) => {
    const amount = budgetInputs[center];
    if (!amount) return;
    try {
      await axios.post(`${API}/financeiro/budgets`, { cost_center: center, amount }, { headers: headers() });
      toast.success("Meta salva! Você será avisado por e-mail se estourar.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível salvar a meta.");
    }
  };

  const removeBudget = async (center) => {
    if (!window.confirm(`Remover a meta de "${center}"?`)) return;
    await axios.delete(`${API}/financeiro/budgets/${encodeURIComponent(center)}`, { headers: headers() });
    await load();
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const selectPendentes = () => setSelected(txs.filter((t) => t.status !== "quitado").map((t) => t.id));

  const bulkSettle = async () => {
    try {
      const r = await axios.post(`${API}/financeiro/transactions/bulk-status`, { ids: selected, status: "quitado" }, { headers: headers() });
      const today = new Date().toISOString().slice(0, 10);
      setTxs((xs) => xs.map((x) => (selected.includes(x.id) ? { ...x, status: "quitado", paid_date: today } : x)));
      toast.success(`${r.data.updated} lançamento(s) marcados como realizados.`);
      setSelected([]);
    } catch {
      toast.error("Não foi possível conciliar.");
    }
  };

  const saveSaldo = async () => {
    try {
      const r = await axios.put(`${API}/financeiro/settings`, { initial_balance: saldoInput }, { headers: headers() });
      setSettings(r.data);
      toast.success("Saldo inicial salvo! A previsão agora parte do seu caixa real.");
    } catch {
      toast.error("Não foi possível salvar o saldo.");
    }
  };

  const uploadContract = async (dealId, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setContractUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/upload`, fd, { headers: headers() });
      await axios.put(`${API}/crm/deals/${dealId}`, { contract_url: up.data.url }, { headers: headers() });
      setDeals((ds) => ds.map((d) => (d.id === dealId ? { ...d, contract_url: up.data.url } : d)));
      toast.success("Contrato anexado ao negócio!");
    } catch {
      toast.error("Falha ao anexar o contrato.");
    } finally {
      setContractUploading(false);
    }
  };

  const addSigner = async (dealId) => {
    const name = (signerNames[dealId] || "").trim();
    if (!name) return;
    try {
      const r = await axios.post(`${API}/crm/deals/${dealId}/signers`, { name }, { headers: headers() });
      setDeals((ds) => ds.map((d) => (d.id === dealId ? { ...d, signers: [...(d.signers || []), r.data] } : d)));
      setSignerNames((s) => ({ ...s, [dealId]: "" }));
    } catch {
      toast.error("Não foi possível adicionar o signatário.");
    }
  };

  const toggleSigner = async (dealId, signer) => {
    try {
      const r = await axios.put(`${API}/crm/deals/${dealId}/signers/${signer.id}`, {}, { headers: headers() });
      setDeals((ds) =>
        ds.map((d) =>
          d.id === dealId
            ? { ...d, signers: (d.signers || []).map((s) => (s.id === signer.id ? { ...s, status: r.data.status, signed_at: r.data.signed_at } : s)) }
            : d
        )
      );
    } catch {
      toast.error("Não foi possível atualizar a assinatura.");
    }
  };

  const removeSigner = async (dealId, signerId) => {
    await axios.delete(`${API}/crm/deals/${dealId}/signers/${signerId}`, { headers: headers() });
    setDeals((ds) => ds.map((d) => (d.id === dealId ? { ...d, signers: (d.signers || []).filter((s) => s.id !== signerId) } : d)));
  };

  const csRowsFor = (d) =>
    csRows[d.id] ||
    (d.signers?.length ? d.signers.map((s) => ({ name: s.name, email: "" })) : [{ name: "", email: "" }]);

  const csSend = async (d) => {
    const rows = csRowsFor(d).filter((r) => r.name.trim() && r.email.trim());
    if (!rows.length) {
      toast.error("Adicione signatários com nome e e-mail.");
      return;
    }
    setCsSending(true);
    try {
      await axios.post(`${API}/crm/deals/${d.id}/clicksign`, { signers: rows }, { headers: headers() });
      toast.success("Contrato enviado para assinatura! Os signatários receberão um e-mail.");
      await load();
    } catch (err) {
      toast.error(apiError(err, "Falha ao enviar para a Clicksign."));
    } finally {
      setCsSending(false);
    }
  };

  const csRefresh = async (d) => {
    try {
      const r = await axios.get(`${API}/crm/deals/${d.id}/clicksign/status`, { headers: headers() });
      toast.success(`Status do envelope: ${r.data.status}`);
      await load();
    } catch (err) {
      toast.error(apiError(err, "Não foi possível consultar o status."));
    }
  };

  const downloadPdf = async () => {
    if (!costPropId) return;
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/financeiro/relatorio/${costPropId}`, { headers: headers(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `resumo-financeiro-${costPropId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const exportFile = async (format) => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/financeiro/extrato`, {
        headers: headers(),
        params: { from_date: exFrom, to_date: exTo, format, status: exStatus, category: exCat, kind: exKind, supplier: exSupplier, cost_center: exCenter },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const fKind = exKind !== "todos" ? `-${exKind === "pagar" ? "a-pagar" : "a-receber"}` : "";
      const fStatus = exStatus !== "todos" ? `-${exStatus === "quitado" ? "realizados" : "pendentes"}` : "";
      const fCat = exCat !== "todas" ? `-${exCat.toLowerCase().replace(/\W+/g, "-")}` : "";
      const fSup = exSupplier !== "todos" ? `-${exSupplier.toLowerCase().replace(/\W+/g, "-")}` : "";
      const fCen = exCenter !== "todos" ? `-${exCenter.toLowerCase().replace(/\W+/g, "-")}` : "";
      a.download = `extrato-${exFrom}-a-${exTo}${fKind}${fStatus}${fCat}${fSup}${fCen}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Não foi possível exportar o extrato.");
    } finally {
      setExporting(false);
    }
  };

  // ---------- dados derivados ----------
  const aReceberComissao = (deals || []).filter((d) => d.status !== "recebida").reduce((s, d) => s + (d.commission_value || 0), 0);
  const recebidasComissao = (deals || []).filter((d) => d.status === "recebida").reduce((s, d) => s + (d.commission_value || 0), 0);
  const porCorretor = {};
  (deals || []).forEach((d) => {
    const k = d.broker_email || "Sem corretor";
    porCorretor[k] = (porCorretor[k] || 0) + (d.commission_value || 0);
  });

  const pendPagar = txs.filter((t) => t.kind === "pagar" && t.status !== "quitado").reduce((s, t) => s + t.amount_value, 0);
  const pendReceber = txs.filter((t) => t.kind === "receber" && t.status !== "quitado").reduce((s, t) => s + t.amount_value, 0);

  const costProp = properties.find((p) => p.id === costPropId);
  const costExpenses = txs.filter((t) => t.kind === "pagar" && t.property_id === costPropId);
  const aquisicao = parseNum(costProp?.purchase_price);
  const totalDespesas = costExpenses.reduce((s, t) => s + t.amount_value, 0);
  const totalInvestido = aquisicao + totalDespesas;
  const dealDoImovel = (deals || []).find((d) => d.property_id === costPropId);
  const valorVenda = dealDoImovel ? parseNum(dealDoImovel.sale_value) : 0;
  const lucroReal = dealDoImovel && totalInvestido > 0 ? valorVenda - totalInvestido : null;

  const dash = useMemo(() => {
    const filtered = txs.filter((t) => {
      const d = t.due_date || (t.created_at || "").slice(0, 10);
      if (!d || d < applied.from || d > applied.to) return false;
      if (applied.tag && !(t.tags || []).some((tg) => tg.toLowerCase().includes(applied.tag.toLowerCase()))) return false;
      return true;
    });
    const byKind = (kind) => filtered.filter((t) => t.kind === kind);
    const summary = (kind) => {
      const list = byKind(kind);
      const previsto = list.reduce((s, t) => s + t.amount_value, 0);
      const realizado = list.filter((t) => t.status === "quitado").reduce((s, t) => s + t.amount_value, 0);
      return { previsto, realizado, pendente: previsto - realizado, pct: previsto ? Math.round((realizado / previsto) * 100) : 0 };
    };
    const catData = (kind) => {
      const acc = {};
      byKind(kind).forEach((t) => {
        acc[t.category] = (acc[t.category] || 0) + t.amount_value;
      });
      return Object.entries(acc).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    };
    const topParties = (kind) => {
      const acc = {};
      byKind(kind).forEach((t) => {
        const k = t.supplier || "Não informado";
        acc[k] = (acc[k] || 0) + t.amount_value;
      });
      return Object.entries(acc).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 5);
    };
    const days = [];
    const start = new Date(applied.from + "T12:00:00");
    const end = new Date(applied.to + "T12:00:00");
    if ((end - start) / 86400000 <= 93) {
      let accPagar = 0;
      let accReceber = 0;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        accPagar += filtered.filter((t) => t.kind === "pagar" && (t.due_date || "").startsWith(key)).reduce((s, t) => s + t.amount_value, 0);
        accReceber += filtered.filter((t) => t.kind === "receber" && (t.due_date || "").startsWith(key)).reduce((s, t) => s + t.amount_value, 0);
        days.push({ dia: key.slice(8, 10) + "/" + key.slice(5, 7), "A receber": Math.round(accReceber), "A pagar": Math.round(accPagar) });
      }
    }
    return { receber: summary("receber"), pagar: summary("pagar"), catReceber: catData("receber"), catPagar: catData("pagar"), topForn: topParties("pagar"), topCli: topParties("receber"), days };
  }, [txs, applied]);

  const calendar = useMemo(() => {
    const [y, m] = calMonth.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(`${calMonth}-${String(d).padStart(2, "0")}`);
    return cells;
  }, [calMonth]);

  const calRangeActive = Boolean(calFrom || calTo);
  const calRangeTxs = useMemo(() => {
    if (!calFrom && !calTo) return [];
    return txs
      .filter((t) => t.due_date && (!calFrom || t.due_date >= calFrom) && (!calTo || t.due_date <= calTo))
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
  }, [txs, calFrom, calTo]);
  const calRangeTotals = useMemo(
    () => ({
      receber: calRangeTxs.filter((t) => t.kind === "receber").reduce((s, t) => s + t.amount_value, 0),
      pagar: calRangeTxs.filter((t) => t.kind === "pagar").reduce((s, t) => s + t.amount_value, 0),
    }),
    [calRangeTxs]
  );
  const fmtDay = (iso) => iso.split("-").reverse().join("/");

  const shiftCalMonth = (delta) => {
    const [y, m] = calMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setCalMonth(d.toISOString().slice(0, 7));
  };

  const anualData = useMemo(() => {
    const names = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    return names.map((name, i) => {
      const prefix = `${anualYear}-${String(i + 1).padStart(2, "0")}`;
      const receber = txs.filter((t) => t.kind === "receber" && (t.due_date || "").startsWith(prefix)).reduce((s, t) => s + t.amount_value, 0);
      const pagar = txs.filter((t) => t.kind === "pagar" && (t.due_date || "").startsWith(prefix)).reduce((s, t) => s + t.amount_value, 0);
      return { name, "A receber": Math.round(receber), "A pagar": Math.round(pagar), saldo: receber - pagar };
    });
  }, [txs, anualYear]);

  const anualTotals = useMemo(
    () => ({
      receber: anualData.reduce((s, m) => s + m["A receber"], 0),
      pagar: anualData.reduce((s, m) => s + m["A pagar"], 0),
    }),
    [anualData]
  );

  const centros = useMemo(() => {
    const acc = {};
    txs.forEach((t) => {
      const k = t.cost_center || "Sem centro de custo";
      if (!acc[k]) acc[k] = { name: k, receber: 0, pagar: 0, count: 0 };
      acc[k][t.kind === "receber" ? "receber" : "pagar"] += t.amount_value;
      acc[k].count += 1;
    });
    return Object.values(acc).sort((a, b) => b.receber + b.pagar - (a.receber + a.pagar));
  }, [txs]);

  const MONTHS_LONG = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
  const todayStr = new Date().toISOString().slice(0, 10);
  const thisMonth = todayStr.slice(0, 7);
  const splitPend = (kind) => {
    const list = txs.filter((t) => t.kind === kind && t.status !== "quitado");
    const sum = (arr) => arr.reduce((s, t) => s + t.amount_value, 0);
    return {
      total: sum(list),
      vencido: sum(list.filter((t) => t.due_date && t.due_date < todayStr)),
      mes: sum(list.filter((t) => !t.due_date || (t.due_date >= todayStr && t.due_date.slice(0, 7) === thisMonth))),
      futuro: sum(list.filter((t) => t.due_date && t.due_date.slice(0, 7) > thisMonth)),
    };
  };
  const spPagar = splitPend("pagar");
  const spReceber = splitPend("receber");
  const pendMesTotal = (kind) =>
    txs.filter((t) => t.kind === kind && t.status !== "quitado" && (!t.due_date || t.due_date.slice(0, 7) === thisMonth)).reduce((s, t) => s + t.amount_value, 0);
  const saldoMesPend = pendMesTotal("receber") - pendMesTotal("pagar");
  const goTxFilter = (key) => {
    setTxFilter(key);
    setTxPage(1);
    setTimeout(() => document.querySelector('[data-testid="tx-list"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const [txPage, setTxPage] = useState(1);
  const [txSortDesc, setTxSortDesc] = useState(false);

  const sortedTxs = useMemo(() => {
    const isOver = (t) => t.status !== "quitado" && t.due_date && t.due_date < todayStr;
    return [...txs].sort((a, b) => {
      const ao = isOver(a) ? 0 : 1;
      const bo = isOver(b) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      const cmp = (a.due_date || "9999").localeCompare(b.due_date || "9999");
      return txSortDesc ? -cmp : cmp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txs, todayStr, txSortDesc]);

  const [txPerPage, setTxPerPage] = useState(10);
  const [txFilter, setTxFilter] = useState("todas");
  const [txSearch, setTxSearch] = useState("");
  const filteredTxs = useMemo(() => {
    const q = normTxt(txSearch);
    return sortedTxs
      .filter((t) => {
        if (txFilter === "pendentes") return t.status !== "quitado";
        if (txFilter === "realizadas") return t.status === "quitado";
        if (txFilter === "vencidas") return t.status !== "quitado" && t.due_date && t.due_date < todayStr;
        if (txFilter === "mes") return t.status !== "quitado" && (!t.due_date || (t.due_date >= todayStr && t.due_date.slice(0, 7) === thisMonth));
        if (txFilter === "futuro") return t.status !== "quitado" && t.due_date && t.due_date.slice(0, 7) > thisMonth;
        return true;
      })
      .filter((t) => !q || normTxt(`${t.description} ${t.supplier || ""} ${t.category || ""} ${t.cost_center || ""} ${(t.tags || []).join(" ")}`).includes(q));
  }, [sortedTxs, txFilter, txSearch, todayStr, thisMonth]);
  const txTotalPages = Math.max(1, Math.ceil(filteredTxs.length / txPerPage));
  const txPageSafe = Math.min(txPage, txTotalPages);
  const pagedTxs = filteredTxs.slice((txPageSafe - 1) * txPerPage, txPageSafe * txPerPage);
  const txMonthTotals = useMemo(() => {
    const acc = {};
    filteredTxs.forEach((t) => {
      const mk = (t.due_date || "").slice(0, 7) || "sem-data";
      if (!acc[mk]) acc[mk] = { receber: 0, pagar: 0 };
      acc[mk][t.kind === "receber" ? "receber" : "pagar"] += t.amount_value || 0;
    });
    return acc;
  }, [filteredTxs]);
  const monthLabel = (mk) =>
    mk === "sem-data" ? "Sem vencimento" : `${MONTHS_LONG[Number(mk.slice(5, 7)) - 1]} ${mk.slice(0, 4)}`;
  const txPageNumbers = useMemo(() => {
    const start = Math.max(1, Math.min(txPageSafe - 2, txTotalPages - 4));
    return Array.from({ length: Math.min(5, txTotalPages) }, (_, i) => start + i);
  }, [txPageSafe, txTotalPages]);

  const previsao = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 1; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const prefix = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = `${MONTHS_LONG[d.getMonth()]}/${String(d.getFullYear()).slice(2)}`;
      const inMonth = txs.filter((t) => (t.due_date || "").startsWith(prefix) && t.status !== "quitado");
      let recPagar = 0;
      let recReceber = 0;
      txs
        .filter((t) => t.recurring && t.due_date && t.due_date.slice(0, 7) <= prefix)
        .forEach((p) => {
          const hasClone = txs.some((c) => c.recurrence_source_id === p.id && (c.due_date || "").startsWith(prefix));
          if (!hasClone) {
            if (p.kind === "pagar") recPagar += p.amount_value;
            else recReceber += p.amount_value;
          }
        });
      const entradas = inMonth.filter((t) => t.kind === "receber").reduce((s, t) => s + t.amount_value, 0) + recReceber;
      const saidas = inMonth.filter((t) => t.kind === "pagar").reduce((s, t) => s + t.amount_value, 0) + recPagar;
      months.push({ prefix, label, entradas, saidas, saldo: entradas - saidas });
    }
    let acc = settings?.initial_balance_value || 0;
    months.forEach((m) => {
      acc += m.saldo;
      m.acumulado = acc;
    });
    return months;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txs, settings]);

  if (!isAdmin && user) {
    return (
      <main className="mx-auto min-h-screen max-w-7xl px-6 pt-20 pb-24 lg:px-10" data-testid="admin-financeiro">
        <AdminNav user={user} onLogout={logout} />
        <p className="mt-10 rounded-lg bg-white p-10 text-center font-semibold text-seed-deep/60 shadow-sm" data-testid="financeiro-restricted">
          Acesso restrito ao administrador.
        </p>
      </main>
    );
  }

  const SummaryCard = ({ title, data, color, testId }) => (
    <div className="rounded-lg bg-white p-6 shadow-sm" data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-wider text-seed-deep/50">{title}</p>
      <p className="mt-2 font-heading text-3xl font-black" style={{ color }}>{brl(data.previsto)}</p>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-seed-off">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${data.pct}%`, background: color }} />
      </div>
      <div className="mt-3 flex justify-between text-xs">
        <span className="font-semibold text-seed-deep/50">Previsto em aberto<br /><strong className="text-sm text-seed-deep">{brl(data.pendente)}</strong></span>
        <span className="text-right font-semibold text-seed-deep/50">Realizado ({data.pct}%)<br /><strong className="text-sm" style={{ color }}>{brl(data.realizado)}</strong></span>
      </div>
    </div>
  );

  const Donut = ({ title, data, testId }) => (
    <div className="rounded-lg bg-white p-6 shadow-sm" data-testid={testId}>
      <h3 className="mb-2 font-heading text-sm font-bold text-seed-deep">{title}</h3>
      {data.length === 0 ? (
        <p className="py-10 text-center text-sm text-seed-deep/40">Sem dados no período.</p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
              {data.map((_, i) => (
                <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => brl(v)} />
            <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );

  const TopList = ({ title, data, color, testId }) => (
    <div className="rounded-lg bg-white p-6 shadow-sm" data-testid={testId}>
      <h3 className="mb-4 font-heading text-sm font-bold text-seed-deep">{title}</h3>
      {data.length === 0 ? (
        <p className="text-sm text-seed-deep/40">Sem dados no período.</p>
      ) : (
        <div className="space-y-3">
          {data.map((d) => {
            const max = data[0].value || 1;
            return (
              <div key={d.name}>
                <div className="mb-1 flex justify-between text-xs font-semibold">
                  <span className="text-seed-deep/70">{d.name}</span>
                  <span className="text-seed-deep">{brl(d.value)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-seed-off">
                  <div className="h-full rounded-full" style={{ width: `${(d.value / max) * 100}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-6 pt-20 pb-24 lg:px-10" data-testid="admin-financeiro">
      <AdminNav user={user} onLogout={logout} />
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tighter text-seed-deep" data-testid="financeiro-title">
          Controle financeiro
        </h1>
      </div>

      <div className="mb-8 flex w-fit flex-wrap gap-2 rounded-full bg-white p-1.5 shadow-sm" data-testid="financeiro-subtabs">
        {SUBTABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setSubtab(t.key)}
            data-testid={t.testId}
            className={`rounded-full px-5 py-2.5 font-heading text-sm font-bold transition-all duration-300 ${
              subtab === t.key ? "bg-seed-salad text-white shadow-md" : "text-seed-deep/60 hover:text-seed-deep"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subtab === "relatorios" && (
        <div className="mb-8 flex w-fit flex-wrap gap-1.5 rounded-full bg-seed-off p-1" data-testid="fin-report-tabs">
          {REPORT_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setReportTab(t.key)}
              data-testid={t.testId}
              className={`rounded-full px-4 py-2 font-heading text-xs font-bold transition-all duration-300 ${
                reportTab === t.key ? "bg-seed-deep text-seed-off shadow-sm" : "text-seed-deep/50 hover:text-seed-deep"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {deals === null ? (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
        </div>
      ) : effSubtab === "dashboard" ? (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-lg bg-white p-5 shadow-sm" data-testid="dash-filters">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">De</label>
              <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className={`${inputCls} mt-1`} data-testid="dash-from" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Até</label>
              <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className={`${inputCls} mt-1`} data-testid="dash-to" />
            </div>
            <div className="min-w-[180px] flex-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Tag</label>
              <input placeholder="Filtrar por tag..." value={fTag} onChange={(e) => setFTag(e.target.value)} className={`${inputCls} mt-1`} data-testid="dash-tag" />
            </div>
            <button onClick={() => setApplied({ from: fFrom, to: fTo, tag: fTag })} data-testid="dash-buscar" className="flex h-12 items-center gap-2 rounded-full bg-seed-deep px-6 font-heading text-sm font-bold text-seed-off transition-transform hover:scale-[1.03]">
              <Search size={15} /> Buscar
            </button>
            <button onClick={() => { setFFrom(monthStart()); setFTo(monthEnd()); setFTag(""); setApplied({ from: monthStart(), to: monthEnd(), tag: "" }); }} data-testid="dash-limpar" className="flex h-12 items-center gap-2 rounded-full border border-seed-deep/20 px-6 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad">
              <RotateCcw size={15} /> Limpar
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SummaryCard title="Contas a receber" data={dash.receber} color="#1AA886" testId="dash-card-receber" />
            <SummaryCard title="Contas a pagar" data={dash.pagar} color="#E11D48" testId="dash-card-pagar" />
          </div>

          <div className="mt-6 rounded-lg bg-white p-6 shadow-sm" data-testid="dash-chart">
            <h3 className="mb-4 font-heading text-sm font-bold text-seed-deep">Fluxo de caixa acumulado no período</h3>
            {dash.days.length === 0 ? (
              <p className="py-10 text-center text-sm text-seed-deep/40">Sem lançamentos no período (ou período maior que 3 meses).</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dash.days}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#004C3F10" />
                  <XAxis dataKey="dia" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => brl(v)} />
                  <Line type="monotone" dataKey="A receber" stroke="#1AA886" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="A pagar" stroke="#E11D48" strokeWidth={2.5} dot={false} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Donut title="Categorias de contas a receber" data={dash.catReceber} testId="dash-donut-receber" />
            <Donut title="Categorias de contas a pagar" data={dash.catPagar} testId="dash-donut-pagar" />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <TopList title="Top clientes (a receber)" data={dash.topCli} color="#1AA886" testId="dash-top-clientes" />
            <TopList title="Top fornecedores (a pagar)" data={dash.topForn} color="#E11D48" testId="dash-top-fornecedores" />
          </div>
        </>
      ) : effSubtab === "comissoes" ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="financeiro-cards">
            <div className="rounded-lg bg-white p-6 shadow-sm" data-testid="fin-a-receber">
              <Clock4 size={20} className="text-seed-salad" />
              <p className="mt-3 font-heading text-2xl font-black text-seed-deep">{brl(aReceberComissao)}</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-wider text-seed-deep/50">Comissões a receber</p>
            </div>
            <div className="rounded-lg bg-white p-6 shadow-sm" data-testid="fin-recebidas">
              <CircleDollarSign size={20} className="text-seed-salad" />
              <p className="mt-3 font-heading text-2xl font-black text-seed-deep">{brl(recebidasComissao)}</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-wider text-seed-deep/50">Comissões recebidas</p>
            </div>
            <div className="rounded-lg bg-white p-6 shadow-sm" data-testid="fin-negocios">
              <Trophy size={20} className="text-seed-salad" />
              <p className="mt-3 font-heading text-2xl font-black text-seed-deep">{deals.length}</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-wider text-seed-deep/50">Negócios fechados</p>
            </div>
          </div>

          <div className="mt-8 rounded-lg bg-white p-8 shadow-sm" data-testid="commission-simulator">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-seed-deep">
              <Calculator size={18} className="text-seed-salad" /> Simulador de comissão
            </h2>
            <p className="mt-1 text-xs text-seed-deep/50">Digite os valores e veja na hora quanto o corretor e a imobiliária ganham.</p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Corretor</label>
                <select
                  value={simBroker}
                  onChange={(e) => {
                    setSimBroker(e.target.value);
                    const b = brokers.find((x) => x.email === e.target.value);
                    if (b) setSimBrokerPct(String(b.commission_share ?? 50));
                  }}
                  className={`${inputCls} mt-1`}
                  data-testid="sim-broker-select"
                >
                  <option value="">Genérico</option>
                  {brokers.map((b) => (
                    <option key={b.email} value={b.email}>{b.name || b.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Valor da venda</label>
                <input inputMode="decimal" placeholder="ex.: 890.000,00" value={simSale} onChange={(e) => setSimSale(maskMoney(e.target.value))} className={`${inputCls} mt-1`} data-testid="sim-sale" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Comissão (% ou R$)</label>
                <input inputMode="decimal" placeholder="ex.: 6% ou 30.000,00" value={simCommission} onChange={(e) => setSimCommission(maskCommission(e.target.value))} className={`${inputCls} mt-1`} data-testid="sim-commission" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Participação do corretor (%)</label>
                <input inputMode="decimal" placeholder="ex.: 50" value={simBrokerPct} onChange={(e) => setSimBrokerPct(maskMoney(e.target.value))} className={`${inputCls} mt-1`} data-testid="sim-broker-pct" />
                <div className="mt-1.5 flex gap-1.5">
                  {["30", "40", "50"].map((p) => (
                    <button key={p} type="button" onClick={() => setSimBrokerPct(p)} data-testid={`sim-preset-${p}`} className={`rounded-full px-3 py-1 text-[10px] font-bold transition-colors ${normTxt(simBrokerPct) === p ? "bg-seed-salad/25 text-seed-deep" : "bg-seed-off text-seed-deep/60 hover:bg-seed-salad/20 hover:text-seed-deep"}`}>
                      {p}%
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-seed-off p-5" data-testid="sim-total">
                <p className="font-heading text-xl font-black text-seed-deep">{brl(simTotal)}</p>
                <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Comissão total</p>
              </div>
              <div className="rounded-lg bg-seed-salad/10 p-5" data-testid="sim-broker">
                <p className="font-heading text-xl font-black text-seed-salad">{brl(simBrokerValue)}</p>
                <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">{simBrokerName ? `${simBrokerName} recebe` : "Corretor recebe"} ({simPct}%)</p>
              </div>
              <div className="rounded-lg bg-seed-deep/5 p-5" data-testid="sim-agency">
                <p className="font-heading text-xl font-black text-seed-deep">{brl(simAgencyValue)}</p>
                <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Imobiliária fica ({100 - simPct}%)</p>
              </div>
            </div>
          </div>

          <form onSubmit={createDeal} className="mt-8 rounded-lg bg-white p-8 shadow-sm" data-testid="deal-form">
            <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-seed-deep">
              <HandCoins size={18} className="text-seed-salad" /> Registrar negócio ganho
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <select required value={form.lead_id} onChange={set("lead_id")} className={inputCls} data-testid="df-lead">
                <option value="">Contato...</option>
                {leads.map((l) => (
                  <option key={l.id} value={l.id}>{l.nome}</option>
                ))}
              </select>
              <select value={form.property_id} onChange={set("property_id")} className={inputCls} data-testid="df-property">
                <option value="">Imóvel (opcional)...</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
              <input required inputMode="decimal" placeholder="Valor da venda (ex.: 890.000,00)" value={form.sale_value} onChange={(e) => setForm((f) => ({ ...f, sale_value: maskMoney(e.target.value) }))} className={inputCls} data-testid="df-sale-value" />
              <input required inputMode="decimal" placeholder="Comissão (ex.: 6% ou 30.000,00)" value={form.commission} onChange={(e) => setForm((f) => ({ ...f, commission: maskCommission(e.target.value) }))} className={inputCls} data-testid="df-commission" />
              <select value={form.broker_email} onChange={set("broker_email")} className={inputCls} data-testid="df-broker">
                <option value="">Corretor...</option>
                {users.map((u) => (
                  <option key={u.email} value={u.email}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
            {(() => {
              const sale = parseNum(form.sale_value);
              const total = form.commission.trim().endsWith("%")
                ? sale * (parseNum(form.commission.replace("%", "")) / 100)
                : parseNum(form.commission);
              if (!sale || !total) return null;
              const broker = users.find((u) => u.email === form.broker_email);
              const pct = broker?.commission_share ?? 50;
              const brokerVal = total * (pct / 100);
              return (
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md bg-seed-off px-5 py-3 text-sm" data-testid="df-split-preview">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Divisão sugerida</span>
                  <span className="font-semibold text-seed-deep/70">Comissão total <strong className="font-heading text-seed-deep">{brl(total)}</strong></span>
                  <span className="font-semibold text-seed-salad">
                    {broker ? (broker.name || broker.email).split(" ")[0] : "Corretor"} recebe <strong className="font-heading">{brl(brokerVal)}</strong> ({pct}%)
                  </span>
                  <span className="font-semibold text-seed-deep/70">Imobiliária fica <strong className="font-heading text-seed-deep">{brl(total - brokerVal)}</strong></span>
                  {broker && broker.commission_share == null && (
                    <span className="text-[10px] font-semibold text-seed-deep/40">(padrão 50% — personalize em Equipe)</span>
                  )}
                </div>
              );
            })()}
            <button type="submit" disabled={saving} data-testid="df-submit" className="mt-5 rounded-full bg-seed-deep px-8 py-3.5 font-heading text-sm font-bold text-seed-off transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60">
              {saving ? "Registrando..." : "Registrar negócio"}
            </button>
          </form>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            <div className="space-y-3" data-testid="deal-list">
              {deals.length === 0 ? (
                <p className="rounded-lg bg-white p-10 text-center text-seed-deep/50 shadow-sm">Nenhum negócio registrado ainda.</p>
              ) : (
                deals.map((d) => (
                  <div key={d.id} className={`rounded-lg bg-white p-5 shadow-sm ${d.status === "recebida" ? "opacity-60" : ""}`} data-testid={`deal-item-${d.id.slice(0, 8)}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-heading text-sm font-bold text-seed-deep">{leadName(d.lead_id)}</p>
                        <p className="mt-0.5 text-xs text-seed-deep/50">
                          {propTitle(d.property_id) || "Sem imóvel"} · {d.broker_email ? userName(d.broker_email) : "Sem corretor"}
                        </p>
                        <p className="mt-1 text-sm text-seed-deep/70">
                          Venda {d.sale_value} · <span className="font-black text-seed-salad">comissão {brl(d.commission_value)}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleDeal(d)}
                          data-testid={`deal-toggle-${d.id.slice(0, 8)}`}
                          className={`rounded-full px-4 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                            d.status === "recebida" ? "bg-seed-deep text-seed-off" : "bg-seed-vivid text-seed-deep"
                          }`}
                        >
                          {d.status === "recebida" ? "Recebida" : "A receber"}
                        </button>
                        <button onClick={() => removeDeal(d.id)} data-testid={`deal-delete-${d.id.slice(0, 8)}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-red-500 hover:text-white" aria-label="Excluir">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                    {d.cost_total != null && (
                      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 rounded-md bg-seed-off px-4 py-3 text-xs" data-testid={`deal-profit-${d.id.slice(0, 8)}`}>
                        <span className="font-semibold text-seed-deep/60">Custo total (aquisição + despesas): <strong className="text-seed-deep">{brl(d.cost_total)}</strong></span>
                        <span className="font-semibold text-seed-deep/60">
                          Lucro real:{" "}
                          <strong className={d.profit >= 0 ? "text-seed-salad" : "text-red-500"}>{brl(d.profit)}</strong>
                        </span>
                      </div>
                    )}
                    <div className="mt-4 border-t border-seed-deep/10 pt-3">
                      <button
                        onClick={() => setExpandedDeal(expandedDeal === d.id ? null : d.id)}
                        data-testid={`deal-contract-toggle-${d.id.slice(0, 8)}`}
                        className="flex items-center gap-2 text-xs font-bold text-seed-deep/60 transition-colors hover:text-seed-salad"
                      >
                        <FileSignature size={14} />
                        Contrato e assinaturas
                        {(d.signers || []).length > 0 && (
                          <span className="text-seed-deep/40">
                            · {(d.signers || []).filter((s) => s.status === "assinado").length}/{(d.signers || []).length} assinaram
                          </span>
                        )}
                      </button>
                      {expandedDeal === d.id && (
                        <div className="mt-3 space-y-3 rounded-md bg-seed-off p-4" data-testid={`deal-contract-${d.id.slice(0, 8)}`}>
                          <div className="flex flex-wrap items-center gap-3">
                            {d.contract_url ? (
                              <a href={d.contract_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-full bg-seed-deep px-4 py-2 text-xs font-bold text-seed-off" data-testid={`deal-contract-view-${d.id.slice(0, 8)}`}>
                                <Paperclip size={13} /> Ver contrato
                              </a>
                            ) : (
                              <span className="text-xs text-seed-deep/40">Nenhum contrato anexado.</span>
                            )}
                            <label className="flex cursor-pointer items-center gap-2 rounded-full border border-seed-deep/20 px-4 py-2 text-xs font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad" data-testid={`deal-contract-upload-${d.id.slice(0, 8)}`}>
                              <input type="file" hidden accept="application/pdf,image/jpeg,image/png" onChange={(e) => uploadContract(d.id, e)} />
                              {contractUploading ? "Enviando..." : d.contract_url ? "Substituir contrato" : "Anexar contrato (PDF)"}
                            </label>
                          </div>
                          <div className="space-y-2" data-testid={`deal-signers-${d.id.slice(0, 8)}`}>
                            {(d.signers || []).map((s) => (
                              <div key={s.id} className="flex items-center justify-between rounded-md bg-white px-4 py-2.5">
                                <span className="text-sm font-semibold text-seed-deep">{s.name}</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => toggleSigner(d.id, s)}
                                    data-testid={`signer-toggle-${s.id.slice(0, 8)}`}
                                    className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                      s.status === "assinado" ? "bg-seed-salad text-white" : "bg-seed-off text-seed-deep/50"
                                    }`}
                                  >
                                    {s.status === "assinado" ? "Assinado" : "Pendente"}
                                  </button>
                                  <button onClick={() => removeSigner(d.id, s.id)} className="text-red-400 hover:text-red-600" aria-label={`Remover ${s.name}`}>
                                    <X size={13} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              placeholder="Nome do signatário (ex.: comprador, vendedor)"
                              value={signerNames[d.id] || ""}
                              onChange={(e) => setSignerNames((s) => ({ ...s, [d.id]: e.target.value }))}
                              className="h-10 flex-1 rounded-md bg-white px-3 text-xs text-seed-deep outline-none focus:shadow-[0_0_0_2px_#00D765]"
                              data-testid={`signer-input-${d.id.slice(0, 8)}`}
                            />
                            <button onClick={() => addSigner(d.id)} data-testid={`signer-add-${d.id.slice(0, 8)}`} className="rounded-full bg-seed-deep px-5 text-xs font-bold text-seed-off transition-transform hover:scale-105">
                              Adicionar
                            </button>
                          </div>

                          <div className="border-t border-seed-deep/10 pt-3" data-testid={`clicksign-box-${d.id.slice(0, 8)}`}>
                            <p className="text-[11px] font-bold uppercase tracking-wider text-seed-deep/50">
                              Assinatura eletrônica com validade jurídica (Clicksign)
                            </p>
                            {d.clicksign?.envelope_id ? (
                              <div className="mt-2 space-y-2">
                                <p className="text-xs text-seed-deep/70">
                                  Envelope <strong>{d.clicksign.envelope_id?.slice(0, 8)}</strong> · status:{" "}
                                  <strong className="text-seed-salad">{d.clicksign.status}</strong>
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {(d.clicksign.signers || []).map((s) => (
                                    <span key={s.email} className={`rounded-full px-3 py-1 text-[10px] font-bold ${s.status === "assinado" ? "bg-seed-salad text-white" : "bg-white text-seed-deep/50"}`}>
                                      {s.name} · {s.status}
                                    </span>
                                  ))}
                                </div>
                                <button onClick={() => csRefresh(d)} data-testid={`clicksign-refresh-${d.id.slice(0, 8)}`} className="rounded-full border border-seed-deep/20 px-4 py-2 text-xs font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad">
                                  Atualizar status
                                </button>
                              </div>
                            ) : (
                              <div className="mt-2 space-y-2">
                                {csRowsFor(d).map((row, i) => (
                                  <div key={i} className="flex gap-2">
                                    <input
                                      placeholder="Nome"
                                      value={row.name}
                                      onChange={(e) => setCsRows((m) => ({ ...m, [d.id]: csRowsFor(d).map((r, j) => (j === i ? { ...r, name: e.target.value } : r)) }))}
                                      className="h-10 flex-1 rounded-md bg-white px-3 text-xs text-seed-deep outline-none focus:shadow-[0_0_0_2px_#00D765]"
                                      data-testid={`clicksign-name-${d.id.slice(0, 8)}-${i}`}
                                    />
                                    <input
                                      placeholder="E-mail para assinar"
                                      type="email"
                                      value={row.email}
                                      onChange={(e) => setCsRows((m) => ({ ...m, [d.id]: csRowsFor(d).map((r, j) => (j === i ? { ...r, email: e.target.value } : r)) }))}
                                      className="h-10 flex-1 rounded-md bg-white px-3 text-xs text-seed-deep outline-none focus:shadow-[0_0_0_2px_#00D765]"
                                      data-testid={`clicksign-email-${d.id.slice(0, 8)}-${i}`}
                                    />
                                  </div>
                                ))}
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => setCsRows((m) => ({ ...m, [d.id]: [...csRowsFor(d), { name: "", email: "" }] }))}
                                    data-testid={`clicksign-addrow-${d.id.slice(0, 8)}`}
                                    className="rounded-full border border-seed-deep/20 px-4 py-2 text-xs font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad"
                                  >
                                    + Signatário
                                  </button>
                                  <button
                                    onClick={() => csSend(d)}
                                    disabled={csSending}
                                    data-testid={`clicksign-send-${d.id.slice(0, 8)}`}
                                    className="rounded-full bg-seed-vivid px-4 py-2 text-xs font-bold text-seed-deep transition-transform hover:scale-105 disabled:opacity-60"
                                  >
                                    {csSending ? "Enviando..." : "Enviar para assinatura"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="h-fit rounded-lg bg-white p-6 shadow-sm" data-testid="fin-by-broker">
              <h3 className="mb-4 font-heading text-sm font-bold text-seed-deep">Total por corretor</h3>
              {Object.keys(porCorretor).length === 0 ? (
                <p className="text-sm text-seed-deep/40">Sem dados ainda.</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(porCorretor).map(([email, total]) => (
                    <div key={email} className="flex items-center justify-between border-b border-seed-deep/10 pb-2 text-sm">
                      <span className="font-semibold text-seed-deep/70">{email === "Sem corretor" ? email : userName(email)}</span>
                      <span className="font-black text-seed-deep">{brl(total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ) : effSubtab === "contas" ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="contas-cards">
            <div className="rounded-lg bg-white p-5 shadow-sm" data-testid="contas-a-pagar">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-seed-deep/50"><ArrowDownCircle size={15} className="text-red-400" /> A pagar (pendente)</p>
              <button type="button" onClick={() => goTxFilter("vencidas")} className="mt-3 block text-left" data-testid="contas-pagar-vencido" title="Ver lançamentos vencidos">
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-500/70">Vencido — exige ação</span>
                <span className="block font-heading text-2xl font-black text-red-500">{brl(spPagar.vencido)}</span>
              </button>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
                <button type="button" onClick={() => goTxFilter("mes")} className="text-left" data-testid="contas-pagar-mes" title="Ver o que vence este mês">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/40">Este mês</span>
                  <span className="block font-heading text-base font-black text-seed-deep">{brl(spPagar.mes)}</span>
                </button>
                <button type="button" onClick={() => goTxFilter("futuro")} className="text-left" data-testid="contas-pagar-futuro" title="Ver parcelas e contas futuras">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/40">Futuro</span>
                  <span className="block font-heading text-base font-black text-seed-deep/50">{brl(spPagar.futuro)}</span>
                </button>
              </div>
              <p className="mt-3 border-t border-seed-deep/5 pt-2 text-[10px] font-semibold text-seed-deep/40">Total pendente geral: {brl(spPagar.total)}</p>
            </div>
            <div className="rounded-lg bg-white p-5 shadow-sm" data-testid="contas-a-receber">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-seed-deep/50"><ArrowUpCircle size={15} className="text-seed-salad" /> A receber (pendente)</p>
              <button type="button" onClick={() => goTxFilter("vencidas")} className="mt-3 block text-left" data-testid="contas-receber-vencido" title="Ver recebíveis vencidos">
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-500/70">Vencido — exige ação</span>
                <span className="block font-heading text-2xl font-black text-red-500">{brl(spReceber.vencido)}</span>
              </button>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
                <button type="button" onClick={() => goTxFilter("mes")} className="text-left" data-testid="contas-receber-mes" title="Ver o que entra este mês">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/40">Este mês</span>
                  <span className="block font-heading text-base font-black text-seed-salad">{brl(spReceber.mes)}</span>
                </button>
                <button type="button" onClick={() => goTxFilter("futuro")} className="text-left" data-testid="contas-receber-futuro" title="Ver recebíveis futuros">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/40">Futuro</span>
                  <span className="block font-heading text-base font-black text-seed-deep/50">{brl(spReceber.futuro)}</span>
                </button>
              </div>
              <p className="mt-3 border-t border-seed-deep/5 pt-2 text-[10px] font-semibold text-seed-deep/40">Total pendente geral: {brl(spReceber.total)}</p>
            </div>
            <div className="rounded-lg bg-seed-deep p-5 text-seed-off shadow-sm" data-testid="contas-saldo-mes">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-seed-off/60"><Wallet size={15} /> Saldo previsto do mês</p>
              <p className={`mt-3 font-heading text-2xl font-black ${saldoMesPend >= 0 ? "text-seed-vivid" : "text-red-300"}`} data-testid="contas-saldo-mes-valor">{brl(saldoMesPend)}</p>
              <p className="mt-2 text-[10px] font-semibold capitalize text-seed-off/50">
                {monthLabel(thisMonth)} · a receber {brl(pendMesTotal("receber"))} − a pagar {brl(pendMesTotal("pagar"))} (só pendentes)
              </p>
            </div>
          </div>

          {!showTxForm && !editingId && (
            <button
              type="button"
              onClick={() => { setShowTxForm(true); setShowMoreTx(false); }}
              data-testid="tx-open-form"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-seed-salad/40 bg-white py-6 font-heading text-sm font-bold text-seed-salad transition-colors hover:border-seed-salad hover:bg-seed-salad/5"
            >
              <Plus size={17} strokeWidth={2.5} /> Novo lançamento
            </button>
          )}

          {(showTxForm || editingId) && (
          <form onSubmit={saveTx} className="mt-8 rounded-lg bg-white p-8 shadow-sm" data-testid="tx-form">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-seed-deep">
                {editingId ? <Pencil size={18} className="text-seed-salad" /> : <Plus size={18} className="text-seed-salad" />}
                {editingId ? "Editar lançamento" : "Novo lançamento"}
              </h2>
              <button type="button" onClick={cancelTxEdit} aria-label="Fechar" data-testid="tx-form-close" className="text-seed-deep/40 transition-colors hover:text-red-500">
                <X size={18} />
              </button>
            </div>
            <div className="mb-4 grid grid-cols-2 gap-3" data-testid="tx-kind">
              <button
                type="button"
                onClick={() => setTxForm((f) => ({ ...f, kind: "pagar", category: (cats.pagar || CATS.pagar)[0] }))}
                data-testid="tx-kind-pagar"
                className={`flex items-center justify-center gap-2 rounded-md border-2 py-3.5 font-heading text-sm font-bold transition-colors ${txForm.kind === "pagar" ? "border-red-400 bg-red-50 text-red-500" : "border-seed-deep/10 text-seed-deep/40 hover:border-red-200 hover:text-red-400"}`}
              >
                <ArrowDownCircle size={17} /> A pagar
              </button>
              <button
                type="button"
                onClick={() => setTxForm((f) => ({ ...f, kind: "receber", category: (cats.receber || CATS.receber)[0] }))}
                data-testid="tx-kind-receber"
                className={`flex items-center justify-center gap-2 rounded-md border-2 py-3.5 font-heading text-sm font-bold transition-colors ${txForm.kind === "receber" ? "border-seed-salad bg-seed-salad/10 text-seed-salad" : "border-seed-deep/10 text-seed-deep/40 hover:border-seed-salad/50 hover:text-seed-salad"}`}
              >
                <ArrowUpCircle size={17} /> A receber
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <input required placeholder="Descrição (ex.: ITBI da Casa Moderna)" value={txForm.description} onChange={setTx("description")} className={inputCls} data-testid="tx-description" />
              <input required inputMode="decimal" placeholder="Valor previsto (ex.: 4.500,90)" value={txForm.amount} onChange={(e) => setTxForm((f) => ({ ...f, amount: maskMoney(e.target.value) }))} className={inputCls} data-testid="tx-amount" />
              <div>
                <input type="date" value={txForm.due_date} onChange={setTx("due_date")} className={inputCls} data-testid="tx-due-date" />
                <p className="mt-1 text-[10px] font-semibold text-seed-deep/40">Data de vencimento</p>
              </div>
              <CategoryPicker
                options={cats[txForm.kind] || CATS[txForm.kind]}
                value={txForm.category}
                onChange={(v) => setTxForm((f) => ({ ...f, category: v }))}
                onCreate={createCategory}
                testId="tx-category"
              />
            </div>
            <button type="button" onClick={() => setShowMoreTx((v) => !v)} data-testid="tx-more-options" className="mt-4 flex items-center gap-1.5 text-xs font-bold text-seed-deep/50 transition-colors hover:text-seed-salad">
              <ChevronDown size={14} className={`transition-transform ${showMoreTx ? "rotate-180" : ""}`} />
              {showMoreTx ? "Menos opções" : "Mais opções (fornecedor, conta, tags, imóvel, parcelas, anexos...)"}
            </button>
            {showMoreTx && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-seed-deep/10 pt-5 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <SupplierPicker
                  known={knownSuppliers}
                  usage={supplierUsage}
                  value={txForm.supplier}
                  onChange={(v) => setTxForm((f) => ({ ...f, supplier: v }))}
                  onRename={quickRenameSupplier}
                  onDelete={quickDeleteSupplier}
                  placeholder={txForm.kind === "pagar" ? "Fornecedor" : "Cliente"}
                  testId="tx-supplier"
                />
                <DupBadge value={txForm.supplier} known={knownSuppliers} testId="tx-supplier-status" />
              </div>
              <AccountPicker
                options={accounts}
                value={txForm.account}
                onChange={(v) => setTxForm((f) => ({ ...f, account: v }))}
                onCreate={createAccount}
                onRename={renameAccount}
                onDelete={deleteAccount}
                testId="tx-account"
              />
              <div>
                <input type="date" value={txForm.paid_date} onChange={setTx("paid_date")} className={inputCls} data-testid="tx-paid-date" />
                <p className="mt-1 text-[10px] font-semibold text-seed-deep/40">Data do pagamento (se já realizado)</p>
              </div>
              <div className="lg:col-span-1">
                <TagPicker
                  known={knownTags}
                  selected={txForm.tagsText.split(",").map((s) => s.trim()).filter(Boolean)}
                  onChange={(arr) => setTxForm((f) => ({ ...f, tagsText: arr.join(", ") }))}
                  testId="tx-tags"
                />
              </div>
              <select value={txForm.property_id} onChange={setTx("property_id")} className={inputCls} data-testid="tx-property">
                <option value="">Vincular a imóvel (opcional)...</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
              <input placeholder="Observações" value={txForm.notes} onChange={setTx("notes")} className={`${inputCls} lg:col-span-2`} data-testid="tx-notes" />
              {!editingId && (
                <div>
                  <input type="number" min="1" max="60" placeholder="Parcelas (ex.: 12)" value={txForm.parcelas} onChange={setTx("parcelas")} className={inputCls} data-testid="tx-installments" />
                  <p className="mt-1 text-[10px] font-semibold text-seed-deep/40">Parcelas — gera todas de uma vez</p>
                </div>
              )}
              {!editingId && (txForm.recurring || Number(txForm.parcelas) > 1) && (
                <div>
                  <select value={txForm.frequency} onChange={setTx("frequency")} className={inputCls} data-testid="tx-frequency">
                    <option value="mensal">Frequência: Mensal</option>
                    <option value="semanal">Frequência: Semanal (a cada 7 dias)</option>
                  </select>
                  <p className="mt-1 text-[10px] font-semibold text-seed-deep/40">Vale para recorrência e parcelas — a 1ª data é a de vencimento acima e cada data gerada pode ser editada depois</p>
                </div>
              )}
              {txForm.recurring && (
                <div>
                  <input type="number" min="1" max="120" placeholder={txForm.frequency === "semanal" ? "Repetir por quantas semanas? (ex.: 12)" : "Repetir por quantos meses? (ex.: 12)"} value={txForm.recurrenceMonths} onChange={setTx("recurrenceMonths")} className={inputCls} data-testid="tx-recurrence-months" />
                  <p className="mt-1 text-[10px] font-semibold text-seed-deep/40">Vazio = repete sem fim. Ex.: 12 gera 12 ocorrências e encerra a série</p>
                </div>
              )}
              <div>
                <input list="tx-center-list" placeholder="Centro de custo (ex.: Empreendimento Vista Verde)" value={txForm.costCenter} onChange={setTx("costCenter")} className={inputCls} data-testid="tx-cost-center" />
                <datalist id="tx-center-list">
                  {knownCenters.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                <DupBadge value={txForm.costCenter} known={knownCenters} testId="tx-cost-center-status" />
              </div>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-seed-salad/50 bg-seed-off/60 px-4 text-xs font-bold text-seed-deep transition-colors hover:text-seed-salad lg:col-span-1" data-testid="tx-attach-button">
                <input type="file" hidden multiple accept="application/pdf,image/jpeg,image/png,image/webp" onChange={attachFiles} data-testid="tx-attach-input" />
                <Paperclip size={15} className="text-seed-salad" />
                {attaching ? "Enviando..." : "Anexar arquivos (PDF/imagem)"}
              </label>
            </div>
            )}
            {txForm.attachments.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="tx-attachments">
                {txForm.attachments.map((url, i) => (
                  <span key={`${url}-${i}`} className="flex items-center gap-2 rounded-full bg-seed-off px-3 py-1.5 text-xs font-semibold text-seed-deep">
                    Anexo {i + 1}
                    <button type="button" onClick={() => setTxForm((f) => ({ ...f, attachments: f.attachments.filter((_, x) => x !== i) }))} className="text-red-400 hover:text-red-600" aria-label={`Remover anexo ${i + 1}`}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button type="submit" disabled={saving || attaching} data-testid="tx-submit" className="rounded-full bg-seed-deep px-8 py-3.5 font-heading text-sm font-bold text-seed-off transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60">
                {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Lançar"}
              </button>
              {!editingId && (
                <button
                  type="button"
                  disabled={saving || attaching}
                  onClick={(ev) => {
                    const frm = ev.currentTarget.closest("form");
                    if (frm && !frm.reportValidity()) return;
                    saveTx(ev, true);
                  }}
                  data-testid="tx-submit-another"
                  className="rounded-full border border-seed-salad/50 px-6 py-3.5 font-heading text-sm font-bold text-seed-salad transition-colors hover:bg-seed-salad/10 disabled:opacity-60"
                >
                  {saving ? "Salvando..." : "Salvar e lançar outro"}
                </button>
              )}
              <button type="button" onClick={cancelTxEdit} data-testid="tx-cancel-edit" className="rounded-full border border-seed-deep/20 px-6 py-3.5 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-red-400 hover:text-red-500">
                Cancelar
              </button>
            </div>
            <label className="ml-5 inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-seed-deep/70" data-testid="tx-recurring-label">
              <input
                type="checkbox"
                checked={txForm.recurring}
                onChange={(e) => setTxForm((f) => ({ ...f, recurring: e.target.checked }))}
                className="h-4 w-4 accent-seed-salad"
                data-testid="tx-recurring"
              />
              Repetir todo mês (recorrente — ex.: aluguel, condomínio)
            </label>
          </form>
          )}

          <p className="mt-10 mb-3 text-xs font-bold uppercase tracking-widest text-seed-deep/40">Extrato e exportação</p>
          <div className="flex flex-wrap items-end gap-3 rounded-lg bg-white p-5 shadow-sm" data-testid="export-bar">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Extrato de</label>
              <input type="date" value={exFrom} onChange={(e) => setExFrom(e.target.value)} className={`${inputCls} mt-1`} data-testid="export-from" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Até</label>
              <input type="date" value={exTo} onChange={(e) => setExTo(e.target.value)} className={`${inputCls} mt-1`} data-testid="export-to" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Tipo</label>
              <select value={exKind} onChange={(e) => setExKind(e.target.value)} className={`${inputCls} mt-1`} data-testid="export-kind">
                <option value="todos">Todos</option>
                <option value="receber">Contas a receber</option>
                <option value="pagar">Contas a pagar</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Status</label>
              <select value={exStatus} onChange={(e) => setExStatus(e.target.value)} className={`${inputCls} mt-1`} data-testid="export-status">
                <option value="todos">Todos</option>
                <option value="pendente">Pendente</option>
                <option value="quitado">Realizado</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Categoria</label>
              <select value={exCat} onChange={(e) => setExCat(e.target.value)} className={`${inputCls} mt-1`} data-testid="export-category">
                <option value="todas">Todas</option>
                {extratoCats.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Fornecedor/Cliente</label>
              <select value={exSupplier} onChange={(e) => setExSupplier(e.target.value)} className={`${inputCls} mt-1`} data-testid="export-supplier">
                <option value="todos">Todos</option>
                {extratoSuppliers.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Centro de custo</label>
              <select value={exCenter} onChange={(e) => setExCenter(e.target.value)} className={`${inputCls} mt-1`} data-testid="export-center">
                <option value="todos">Todos</option>
                {knownCenters.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <button onClick={() => setShowExtrato((v) => !v)} data-testid="export-view" className={`flex h-12 items-center gap-2 rounded-full px-6 font-heading text-sm font-bold transition-colors ${showExtrato ? "bg-seed-salad text-seed-off" : "border border-seed-salad/40 text-seed-salad hover:bg-seed-salad/10"}`}>
              <Search size={15} /> {showExtrato ? "Ocultar extrato" : "Ver na tela"}
            </button>
            <button onClick={() => exportFile("pdf")} disabled={exporting} data-testid="export-pdf" className="flex h-12 items-center gap-2 rounded-full bg-seed-deep px-6 font-heading text-sm font-bold text-seed-off transition-transform hover:scale-[1.03] disabled:opacity-60">
              <FileDown size={15} /> Extrato PDF
            </button>
            <button onClick={() => exportFile("csv")} disabled={exporting} data-testid="export-csv" className="flex h-12 items-center gap-2 rounded-full border border-seed-deep/20 px-6 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad disabled:opacity-60">
              <FileDown size={15} /> Planilha (CSV)
            </button>
          </div>

          {showExtrato && (
            <div className="mt-4 rounded-lg bg-white p-6 shadow-sm" data-testid="extrato-view">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg bg-seed-off p-4" data-testid="extrato-total-entradas">
                  <p className="font-heading text-lg font-black text-seed-salad">{brl(extratoEntradas)}</p>
                  <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Entradas</p>
                </div>
                <div className="rounded-lg bg-seed-off p-4" data-testid="extrato-total-saidas">
                  <p className="font-heading text-lg font-black text-red-500">{brl(extratoSaidas)}</p>
                  <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Saídas</p>
                </div>
                <div className="rounded-lg bg-seed-off p-4" data-testid="extrato-saldo">
                  <p className={`font-heading text-lg font-black ${extratoEntradas - extratoSaidas >= 0 ? "text-seed-deep" : "text-red-500"}`}>{brl(extratoEntradas - extratoSaidas)}</p>
                  <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Saldo do período</p>
                </div>
                <div className="rounded-lg bg-seed-off p-4" data-testid="extrato-count">
                  <p className="font-heading text-lg font-black text-seed-deep">{extratoTxs.length}</p>
                  <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Lançamentos</p>
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm" data-testid="extrato-table">
                  <thead>
                    <tr className="border-b border-seed-deep/10 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">
                      <th className="py-2 pr-3">Vencimento</th>
                      <th className="py-2 pr-3">Tipo</th>
                      <th className="py-2 pr-3">Categoria</th>
                      <th className="py-2 pr-3">Descrição</th>
                      <th className="py-2 pr-3">Fornecedor/Cliente</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extratoTxs.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="py-8 text-center text-seed-deep/40" data-testid="extrato-empty">Nenhum lançamento no período.</td>
                      </tr>
                    ) : (
                      extratoTxs.map((t) => (
                        <tr key={t.id} className="border-b border-seed-deep/5 last:border-0" data-testid={`extrato-row-${t.id.slice(0, 8)}`}>
                          <td className="py-2.5 pr-3 font-semibold text-seed-deep/70">{fmtDate(t.due_date)}</td>
                          <td className="py-2.5 pr-3">
                            {t.kind === "pagar" ? (
                              <span className="flex items-center gap-1 font-bold text-red-500"><ArrowDownCircle size={13} /> Pagar</span>
                            ) : (
                              <span className="flex items-center gap-1 font-bold text-seed-salad"><ArrowUpCircle size={13} /> Receber</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-3 text-seed-deep/70">{t.category || "—"}</td>
                          <td className="py-2.5 pr-3 font-semibold text-seed-deep">{t.description}</td>
                          <td className="py-2.5 pr-3 text-seed-deep/70">{t.supplier || "—"}</td>
                          <td className="py-2.5 pr-3">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${t.status === "quitado" ? "bg-seed-salad/15 text-seed-salad" : "bg-amber-100 text-amber-700"}`}>
                              {t.status === "quitado" ? "Realizado" : "Pendente"}
                            </span>
                          </td>
                          <td className={`py-2.5 text-right font-heading font-black ${t.kind === "pagar" ? "text-red-500" : "text-seed-salad"}`}>{brl(t.amount_value)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3" data-testid="tx-list-header">
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold uppercase tracking-widest text-seed-deep/40" data-testid="tx-count">
                {filteredTxs.length === txs.length ? `${txs.length} lançamento(s)` : `${filteredTxs.length} de ${txs.length} lançamento(s)`}
              </p>
              <button
                onClick={() => setTxSortDesc((v) => !v)}
                data-testid="tx-sort-toggle"
                title={txSortDesc ? "Mais recentes primeiro — clique para inverter" : "Mais antigas primeiro — clique para inverter"}
                aria-label="Inverter ordem das datas"
                className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-seed-deep/60 shadow-sm transition-colors hover:text-seed-salad"
              >
                {txSortDesc ? <ArrowDownWideNarrow size={14} /> : <ArrowUpNarrowWide size={14} />}
              </button>
            </div>
            <button onClick={selectPendentes} data-testid="select-pendentes" className="text-xs font-bold text-seed-salad transition-colors hover:text-seed-deep">
              Selecionar todos os pendentes
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="tx-quick-filters">
            <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-seed-deep/35" />
              <input
                placeholder="Buscar por descrição, fornecedor, categoria..."
                value={txSearch}
                onChange={(e) => { setTxSearch(e.target.value); setTxPage(1); }}
                className="h-11 w-full rounded-full bg-white pl-10 pr-4 text-sm text-seed-deep shadow-sm outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]"
                data-testid="tx-search"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: "todas", label: "Todas" },
                { key: "pendentes", label: "Pendentes" },
                { key: "vencidas", label: "Vencidas" },
                { key: "mes", label: "Este mês" },
                { key: "futuro", label: "Futuro" },
                { key: "realizadas", label: "Realizadas" },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => { setTxFilter(f.key); setTxPage(1); }}
                  data-testid={`tx-filter-${f.key}`}
                  className={`rounded-full px-4 py-2.5 font-heading text-xs font-bold transition-colors ${txFilter === f.key ? "bg-seed-deep text-seed-off" : "bg-white text-seed-deep/60 shadow-sm hover:text-seed-salad"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {selected.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-4 rounded-lg bg-seed-deep p-4 text-seed-off shadow-lg" data-testid="bulk-bar">
              <span className="text-sm font-bold">{selected.length} selecionado(s)</span>
              <button onClick={bulkSettle} data-testid="bulk-settle" className="rounded-full bg-seed-vivid px-5 py-2 font-heading text-xs font-bold text-seed-deep transition-transform hover:scale-105">
                Marcar como realizados
              </button>
              <button onClick={() => setSelected([])} data-testid="bulk-clear" className="text-xs font-bold text-seed-off/70 hover:text-seed-off">
                Limpar seleção
              </button>
            </div>
          )}
          <div className="mt-3 space-y-3" data-testid="tx-list">
            {filteredTxs.length === 0 ? (
              <p className="rounded-lg bg-white p-10 text-center text-seed-deep/50 shadow-sm" data-testid="tx-empty">
                {txs.length === 0 ? "Nenhum lançamento ainda." : "Nenhum lançamento encontrado com esse filtro."}
              </p>
            ) : (
              pagedTxs.map((t, idx) => {
                const vencida = t.status !== "quitado" && t.due_date && t.due_date < todayStr;
                const mk = (t.due_date || "").slice(0, 7) || "sem-data";
                const prevMk = idx > 0 ? ((pagedTxs[idx - 1].due_date || "").slice(0, 7) || "sem-data") : null;
                const mTotals = txMonthTotals[mk];
                const mSaldo = mTotals ? mTotals.receber - mTotals.pagar : 0;
                return (
                <div key={t.id}>
                  {mk !== prevMk && (
                    <div className={`mb-1 flex flex-wrap items-center justify-between gap-2 ${idx === 0 ? "" : "mt-4"}`} data-testid={`tx-month-${mk}`}>
                      <p className="text-xs font-black uppercase tracking-widest text-seed-deep/50 first-letter:capitalize">{monthLabel(mk)}</p>
                      {mTotals && (
                        <p className="text-[11px] font-bold">
                          <span className="text-seed-salad">+{brl(mTotals.receber)}</span>
                          <span className="mx-1.5 text-seed-deep/25">·</span>
                          <span className="text-red-500">−{brl(mTotals.pagar)}</span>
                          <span className="mx-1.5 text-seed-deep/25">·</span>
                          <span className={mSaldo >= 0 ? "text-seed-deep" : "text-red-500"}>Saldo {brl(mSaldo)}</span>
                        </p>
                      )}
                    </div>
                  )}
                <div className={`rounded-lg p-5 shadow-sm ${vencida ? "border-l-4 border-red-500 bg-red-50/70" : "bg-white"} ${t.status === "quitado" ? "opacity-60" : ""}`} data-testid={`tx-item-${t.id.slice(0, 8)}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(t.id)}
                      onChange={() => toggleSelect(t.id)}
                      data-testid={`tx-select-${t.id.slice(0, 8)}`}
                      aria-label={`Selecionar ${t.description}`}
                      className="h-4 w-4 shrink-0 accent-seed-salad"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 font-heading text-sm font-bold text-seed-deep">
                        {t.kind === "pagar" ? <ArrowDownCircle size={15} className="text-red-400" /> : <ArrowUpCircle size={15} className="text-seed-salad" />}
                        {t.description}
                        {vencida && (
                          <span className="rounded-full bg-red-500 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white" data-testid={`tx-vencida-${t.id.slice(0, 8)}`}>
                            Vencida
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-seed-deep/50">
                        {t.category}
                        {t.supplier && ` · ${t.supplier}`}
                        {propTitle(t.property_id) && ` · ${propTitle(t.property_id)}`}
                        {t.due_date && ` · vence ${fmtDate(t.due_date)}`}
                        {t.status === "quitado" && t.paid_date && ` · realizado em ${fmtDate(t.paid_date)}`}
                      </p>
                      {(t.notes || t.recurring || t.cost_center || t.account || (t.tags || []).length > 0 || (t.attachments || []).length > 0) && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {t.recurring && (
                            <span className="rounded-full bg-seed-deep px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-off" data-testid={`tx-recurring-badge-${t.id.slice(0, 8)}`}>
                              {t.frequency === "semanal" ? "Semanal" : "Mensal"}{t.recurrence_months ? ` · ${t.recurrence_months}x` : ""}
                            </span>
                          )}
                          {t.cost_center && (
                            <span className="rounded-full border border-seed-deep/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/60">
                              {t.cost_center}
                            </span>
                          )}
                          {t.account && (
                            <span className="rounded-full bg-seed-off px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">
                              {t.account}
                            </span>
                          )}
                          {(t.tags || []).map((tg) => (
                            <span key={tg} className="rounded-full bg-seed-salad/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-salad">{tg}</span>
                          ))}
                          {(t.attachments || []).map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 rounded-full bg-seed-off px-2.5 py-0.5 text-[10px] font-bold text-seed-deep/60 hover:text-seed-salad">
                              <Paperclip size={10} /> Anexo {i + 1}
                            </a>
                          ))}
                        </div>
                      )}
                      {t.notes && <p className="mt-1.5 text-xs italic text-seed-deep/40">{t.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-heading text-sm font-black ${t.kind === "pagar" ? "text-red-500" : "text-seed-salad"}`}>
                        {t.kind === "pagar" ? "−" : "+"} {brl(t.amount_value)}
                      </span>
                      <button
                        onClick={() => toggleTx(t)}
                        data-testid={`tx-toggle-${t.id.slice(0, 8)}`}
                        className={`rounded-full px-4 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                          t.status === "quitado" ? "bg-seed-deep text-seed-off" : "bg-seed-vivid text-seed-deep"
                        }`}
                      >
                        {t.status === "quitado" ? "Pagamento realizado" : "Ainda não realizado"}
                      </button>
                      <button onClick={() => openTxEdit(t)} data-testid={`tx-edit-${t.id.slice(0, 8)}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-seed-vivid" aria-label="Editar lançamento">
                        <Pencil size={15} />
                      </button>
                      {t.recurring && (
                        <button
                          onClick={() => stopRecurrence(t)}
                          data-testid={`tx-stop-recurrence-${t.id.slice(0, 8)}`}
                          title="Encerrar recorrência (para de gerar os próximos)"
                          aria-label="Encerrar recorrência"
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-amber-400 hover:text-white"
                        >
                          <CircleStop size={15} />
                        </button>
                      )}
                      {(t.recurring || t.recurrence_source_id) && (
                        <button
                          onClick={() => openGroupEdit(t, true)}
                          data-testid={`tx-series-edit-${(t.recurring ? t.id : t.recurrence_source_id).slice(0, 8)}`}
                          title="Editar série recorrente inteira"
                          aria-label="Editar série recorrente"
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-seed-salad hover:text-white"
                        >
                          <RotateCcw size={15} />
                        </button>
                      )}
                      {t.installment_group && (
                        <button
                          onClick={() => openGroupEdit(t)}
                          data-testid={`tx-group-edit-${t.installment_group.slice(0, 8)}`}
                          title="Editar todas as parcelas deste grupo"
                          aria-label="Editar grupo de parcelas"
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-seed-salad hover:text-white"
                        >
                          <ListChecks size={15} />
                        </button>
                      )}
                      {t.installment_group && (
                        <button
                          onClick={() => removeGroup(t)}
                          data-testid={`tx-group-delete-${t.installment_group.slice(0, 8)}`}
                          title="Excluir todas as parcelas deste grupo"
                          aria-label="Excluir grupo de parcelas"
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-red-400 hover:text-white"
                        >
                          <Layers size={15} />
                        </button>
                      )}
                      <button onClick={() => removeTx(t.id)} data-testid={`tx-delete-${t.id.slice(0, 8)}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-red-500 hover:text-white" aria-label="Excluir">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
                </div>
                );
              })
            )}
          </div>
          {filteredTxs.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="tx-pagination">
              <div className="flex items-center gap-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Por página</label>
                <select
                  value={txPerPage}
                  onChange={(e) => { setTxPerPage(Number(e.target.value)); setTxPage(1); }}
                  className="h-9 rounded-md border border-seed-deep/15 bg-white px-2 text-xs font-bold text-seed-deep"
                  data-testid="tx-per-page"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-xs font-semibold text-seed-deep/50" data-testid="tx-page-info">
                  {(txPageSafe - 1) * txPerPage + 1}–{Math.min(txPageSafe * txPerPage, filteredTxs.length)} de {filteredTxs.length}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setTxPage(txPageSafe - 1)}
                  disabled={txPageSafe <= 1}
                  data-testid="tx-page-prev"
                  aria-label="Página anterior"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white font-bold text-seed-deep shadow-sm transition-colors hover:text-seed-salad disabled:opacity-40"
                >
                  ←
                </button>
                {txPageNumbers.map((n) => (
                  <button
                    key={n}
                    onClick={() => setTxPage(n)}
                    data-testid={`tx-page-${n}`}
                    className={`h-9 w-9 rounded-full font-heading text-xs font-bold shadow-sm transition-colors ${n === txPageSafe ? "bg-seed-deep text-seed-off" : "bg-white text-seed-deep hover:text-seed-salad"}`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setTxPage(txPageSafe + 1)}
                  disabled={txPageSafe >= txTotalPages}
                  data-testid="tx-page-next"
                  aria-label="Próxima página"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white font-bold text-seed-deep shadow-sm transition-colors hover:text-seed-salad disabled:opacity-40"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </>
      ) : effSubtab === "previsao" ? (
        <div data-testid="previsao-section">
          <div className="mb-6 flex flex-wrap items-end gap-3 rounded-lg bg-white p-5 shadow-sm" data-testid="saldo-inicial-bar">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Saldo atual da conta (R$)</label>
              <input
                placeholder="ex.: 25000"
                value={saldoInput}
                onChange={(e) => setSaldoInput(e.target.value)}
                className={`${inputCls} mt-1 w-56`}
                data-testid="saldo-input"
              />
            </div>
            <button onClick={saveSaldo} data-testid="saldo-save" className="flex h-12 items-center rounded-full bg-seed-deep px-6 font-heading text-sm font-bold text-seed-off transition-transform hover:scale-[1.03]">
              Salvar saldo
            </button>
            {settings && (
              <p className="pb-3 text-xs font-semibold text-seed-deep/50" data-testid="saldo-info">
                A previsão parte de <strong className="text-seed-deep">{brl(settings.initial_balance_value)}</strong>
              </p>
            )}
          </div>
          <p className="mb-6 max-w-2xl text-sm leading-relaxed text-seed-deep/60">
            Projeção dos próximos 3 meses somando pendências lançadas, parcelas futuras e contas recorrentes (mesmo as que ainda não foram geradas).
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {previsao.map((m) => (
              <div key={m.prefix} className="rounded-lg bg-white p-6 shadow-sm" data-testid={`previsao-${m.prefix}`}>
                <p className="font-heading text-sm font-bold uppercase tracking-wider text-seed-deep/50">{m.label}</p>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-seed-deep/60">Entradas previstas</span><strong className="text-seed-salad">+ {brl(m.entradas)}</strong></div>
                  <div className="flex justify-between"><span className="text-seed-deep/60">Saídas previstas</span><strong className="text-red-500">− {brl(m.saidas)}</strong></div>
                  <div className="flex justify-between border-t border-seed-deep/10 pt-2">
                    <span className="font-bold text-seed-deep/70">Saldo do mês</span>
                    <strong className={`font-heading text-base ${m.saldo >= 0 ? "text-seed-salad" : "text-red-500"}`}>{brl(m.saldo)}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-seed-deep/60">Acumulado</span>
                    <strong className={`font-heading ${m.acumulado >= 0 ? "text-seed-deep" : "text-red-500"}`} data-testid={`previsao-acumulado-${m.prefix}`}>{brl(m.acumulado)}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-lg bg-white p-6 shadow-sm" data-testid="previsao-chart">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-sm font-bold text-seed-deep">
              <TrendingUp size={16} className="text-seed-salad" /> Saldo acumulado projetado
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={previsao.map((m) => ({ name: m.label, Acumulado: Math.round(m.acumulado) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#004C3F10" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => brl(v)} />
                <Bar dataKey="Acumulado" fill="#1AA886" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : effSubtab === "centros" ? (
        <div data-testid="centros-section">
          {centros.length === 0 ? (
            <p className="rounded-lg bg-white p-10 text-center text-seed-deep/50 shadow-sm">Nenhum lançamento ainda.</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {centros.map((c) => {
                const resultado = c.receber - c.pagar;
                const budget = budgets.find((b) => b.cost_center === c.name);
                const monthPrefix = new Date().toISOString().slice(0, 7);
                const monthSpent = txs
                  .filter((t) => t.kind === "pagar" && (t.cost_center || "Sem centro de custo") === c.name && (t.due_date || "").startsWith(monthPrefix))
                  .reduce((s, t) => s + t.amount_value, 0);
                const pct = budget?.amount_value ? Math.min(100, Math.round((monthSpent / budget.amount_value) * 100)) : 0;
                const over = budget?.amount_value && monthSpent > budget.amount_value;
                return (
                  <div key={c.name} className="rounded-lg bg-white p-6 shadow-sm" data-testid={`centro-${c.name.replace(/\W+/g, "-").slice(0, 20)}`}>
                    <p className="font-heading text-sm font-bold text-seed-deep">{c.name}</p>
                    <p className="mt-0.5 text-xs text-seed-deep/40">{c.count} lançamento(s)</p>
                    <div className="mt-4 space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-seed-deep/60">Entradas</span><strong className="text-seed-salad">+ {brl(c.receber)}</strong></div>
                      <div className="flex justify-between"><span className="text-seed-deep/60">Saídas</span><strong className="text-red-500">− {brl(c.pagar)}</strong></div>
                      <div className="flex justify-between border-t border-seed-deep/10 pt-2">
                        <span className="font-bold text-seed-deep/70">Resultado</span>
                        <strong className={`font-heading text-base ${resultado >= 0 ? "text-seed-salad" : "text-red-500"}`}>{brl(resultado)}</strong>
                      </div>
                    </div>
                    {c.name !== "Sem centro de custo" && (
                      <div className="mt-4 border-t border-seed-deep/10 pt-3" data-testid={`centro-budget-${c.name.replace(/\W+/g, "-").slice(0, 20)}`}>
                        {budget && (
                          <div className="mb-3">
                            <div className="flex justify-between text-[11px] font-bold">
                              <span className="uppercase tracking-wider text-seed-deep/50">Meta do mês: {brl(budget.amount_value)}</span>
                              <span className={over ? "text-red-500" : "text-seed-salad"}>{over ? "Estourada!" : `${pct}% usado`}</span>
                            </div>
                            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-seed-off">
                              <div className={`h-full rounded-full transition-all duration-700 ${over ? "bg-red-500" : "bg-seed-vivid"}`} style={{ width: `${over ? 100 : pct}%` }} />
                            </div>
                            <p className="mt-1 text-[10px] text-seed-deep/40">Gasto neste mês: {brl(monthSpent)}</p>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            inputMode="decimal"
                            placeholder="Meta mensal (ex.: 50.000,00)"
                            value={budgetInputs[c.name] ?? (budget ? (budget.amount_value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "")}
                            onChange={(e) => setBudgetInputs((bi) => ({ ...bi, [c.name]: maskMoney(e.target.value) }))}
                            className="h-10 flex-1 rounded-md bg-seed-off px-3 text-xs text-seed-deep outline-none focus:shadow-[0_0_0_2px_#00D765]"
                            data-testid={`centro-budget-input-${c.name.replace(/\W+/g, "-").slice(0, 20)}`}
                          />
                          <button
                            onClick={() => saveBudget(c.name)}
                            data-testid={`centro-budget-save-${c.name.replace(/\W+/g, "-").slice(0, 20)}`}
                            className="rounded-full bg-seed-deep px-4 text-xs font-bold text-seed-off transition-transform hover:scale-105"
                          >
                            Salvar
                          </button>
                          {budget && (
                            <button onClick={() => removeBudget(c.name)} className="rounded-full bg-seed-off px-3 text-xs font-bold text-red-400 hover:text-red-600" aria-label="Remover meta">
                              <X size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-6 text-xs text-seed-deep/40">
            Dica: preencha o campo "Centro de custo" nos lançamentos para agrupar por projeto ou empreendimento e comparar a rentabilidade entre operações.
          </p>
        </div>
      ) : effSubtab === "calendario" ? (
        <div className="rounded-lg bg-white p-6 shadow-sm" data-testid="calendar-section">
          <div className="mb-5 flex items-center justify-between">
            <button onClick={() => shiftCalMonth(-1)} data-testid="cal-prev" className="rounded-full border border-seed-deep/20 px-5 py-2 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad">
              ← Anterior
            </button>
            <h2 className="font-heading text-xl font-black tracking-tight text-seed-deep" data-testid="cal-title">
              <span className="capitalize">{MONTHS_LONG[Number(calMonth.slice(5, 7)) - 1]}</span> de {calMonth.slice(0, 4)}
            </h2>
            <button onClick={() => shiftCalMonth(1)} data-testid="cal-next" className="rounded-full border border-seed-deep/20 px-5 py-2 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad">
              Próximo →
            </button>
          </div>
          <div className="mb-6 grid grid-cols-1 gap-3 rounded-md bg-seed-off/60 p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="cal-filters">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Ir para mês/ano</label>
              <input type="month" value={calMonth} onChange={(e) => e.target.value && setCalMonth(e.target.value)} className={`${inputCls} mt-1 h-10`} data-testid="cal-jump" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">De (vencimento)</label>
              <input type="date" value={calFrom} onChange={(e) => setCalFrom(e.target.value)} className={`${inputCls} mt-1 h-10`} data-testid="cal-from" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Até (vencimento)</label>
              <input type="date" value={calTo} onChange={(e) => setCalTo(e.target.value)} className={`${inputCls} mt-1 h-10`} data-testid="cal-to" />
            </div>
            <div className="flex items-end">
              {calRangeActive ? (
                <button type="button" onClick={() => { setCalFrom(""); setCalTo(""); }} className="flex h-10 items-center gap-2 rounded-full border border-seed-deep/20 px-5 font-heading text-xs font-bold text-seed-deep transition-colors hover:border-red-400 hover:text-red-500" data-testid="cal-clear-range">
                  <RotateCcw size={13} /> Limpar período
                </button>
              ) : (
                <p className="pb-1 text-[11px] font-semibold text-seed-deep/40">Use De/Até para destacar um período e ver o resumo abaixo do calendário.</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
              <p key={d} className="pb-2 text-center text-[10px] font-bold uppercase tracking-widest text-seed-deep/40">{d}</p>
            ))}
            {calendar.map((day, i) => {
              const dayTxs = day ? txs.filter((t) => (t.due_date || "") === day) : [];
              const inRange = day && calRangeActive && (!calFrom || day >= calFrom) && (!calTo || day <= calTo);
              const detailOpen = day && dayTxs.length > 0 && (openDay === day || hoverDay === day);
              const col = i % 7;
              return (
                <div
                  key={i}
                  data-testid={day ? `cal-day-${day}` : undefined}
                  onMouseEnter={() => day && dayTxs.length > 0 && setHoverDay(day)}
                  onMouseLeave={() => setHoverDay(null)}
                  onClick={() => {
                    if (!day || dayTxs.length === 0) return;
                    setOpenDay((d) => (d === day ? null : day));
                  }}
                  className={`relative min-h-[100px] rounded-md p-2 ${
                    day ? "border border-seed-deep/10 bg-seed-off/50" : ""
                  } ${day === todayStr ? "ring-2 ring-seed-vivid" : ""} ${inRange && dayTxs.length > 0 ? "bg-seed-vivid/10 ring-1 ring-seed-vivid/60" : ""} ${day && dayTxs.length > 0 ? "cursor-pointer transition-colors hover:bg-seed-off" : ""}`}
                >
                  {day && (
                    <>
                      <p className={`text-xs font-bold ${day === todayStr ? "text-seed-salad" : "text-seed-deep/60"}`}>{Number(day.slice(8, 10))}</p>
                      <div className="mt-1 space-y-1">
                        {dayTxs.slice(0, 3).map((t) => (
                          <div
                            key={t.id}
                            className={`truncate rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                              t.kind === "pagar" ? "bg-red-50 text-red-600" : "bg-seed-salad/10 text-seed-salad"
                            } ${t.status === "quitado" ? "opacity-50 line-through" : ""}`}
                          >
                            {t.description} · {brl(t.amount_value)}
                          </div>
                        ))}
                        {dayTxs.length > 3 && (
                          <p className="text-[10px] font-bold text-seed-deep/40">+{dayTxs.length - 3} mais · ver todos</p>
                        )}
                      </div>
                      {detailOpen && (
                        <div
                          className={`absolute top-full z-50 mt-1 w-64 max-w-[80vw] rounded-md bg-white p-3 shadow-xl ring-1 ring-seed-deep/10 ${col >= 4 ? "right-0" : "left-0"}`}
                          data-testid={`cal-day-detail-${day}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="font-heading text-xs font-black text-seed-deep">{fmtDay(day)} · {dayTxs.length} lançamento(s)</p>
                            <button type="button" onClick={() => { setOpenDay(null); setHoverDay(null); }} aria-label="Fechar detalhes" data-testid={`cal-day-detail-close-${day}`} className="text-seed-deep/40 transition-colors hover:text-red-500">
                              <X size={14} />
                            </button>
                          </div>
                          <div className="max-h-56 space-y-1.5 overflow-y-auto">
                            {dayTxs.map((t) => (
                              <div key={t.id} className={`rounded px-2 py-1.5 ${t.kind === "pagar" ? "bg-red-50" : "bg-seed-salad/10"}`} data-testid={`cal-detail-tx-${t.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                  <span className={`text-[11px] font-bold ${t.kind === "pagar" ? "text-red-600" : "text-seed-salad"} ${t.status === "quitado" ? "line-through opacity-60" : ""}`}>{t.description}</span>
                                  <span className="shrink-0 text-[11px] font-black text-seed-deep">{brl(t.amount_value)}</span>
                                </div>
                                <p className="mt-0.5 text-[10px] font-semibold text-seed-deep/50">
                                  {t.category || "Sem categoria"}{t.supplier ? ` · ${t.supplier}` : ""} · {t.status === "quitado" ? "Realizado" : "Pendente"}
                                </p>
                              </div>
                            ))}
                          </div>
                          <div className="mt-2 flex items-center justify-between gap-2 border-t border-seed-deep/10 pt-2 text-[10px] font-bold">
                            <span className="text-red-500">A pagar: {brl(dayTxs.filter((t) => t.kind === "pagar").reduce((s, t) => s + t.amount_value, 0))}</span>
                            <span className="text-seed-salad">A receber: {brl(dayTxs.filter((t) => t.kind === "receber").reduce((s, t) => s + t.amount_value, 0))}</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {calRangeActive && (
            <div className="mt-6 rounded-md border border-seed-deep/10 p-5" data-testid="cal-range-results">
              <h3 className="font-heading text-sm font-black text-seed-deep" data-testid="cal-range-title">
                Período {calFrom ? fmtDay(calFrom) : "…"} — {calTo ? fmtDay(calTo) : "…"} · {calRangeTxs.length} lançamento(s)
              </h3>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs font-bold" data-testid="cal-range-totals">
                <span className="text-seed-salad">A receber: {brl(calRangeTotals.receber)}</span>
                <span className="text-red-500">A pagar: {brl(calRangeTotals.pagar)}</span>
                <span className={calRangeTotals.receber - calRangeTotals.pagar >= 0 ? "text-seed-deep" : "text-red-500"}>
                  Saldo: {brl(calRangeTotals.receber - calRangeTotals.pagar)}
                </span>
              </div>
              {calRangeTxs.length === 0 ? (
                <p className="mt-4 text-sm text-seed-deep/40">Nenhum lançamento com vencimento neste período.</p>
              ) : (
                <div className="mt-4 max-h-72 space-y-1.5 overflow-y-auto">
                  {calRangeTxs.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded-md bg-seed-off/60 px-3 py-2" data-testid={`cal-range-tx-${t.id}`}>
                      <span className="text-[11px] font-black text-seed-deep/50">{fmtDay(t.due_date)}</span>
                      <span className={`flex-1 truncate text-xs font-bold ${t.status === "quitado" ? "text-seed-deep/40 line-through" : "text-seed-deep"}`}>{t.description}</span>
                      {t.category && <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-seed-deep/50">{t.category}</span>}
                      <span className={`text-[10px] font-bold uppercase ${t.status === "quitado" ? "text-seed-salad" : "text-amber-500"}`}>{t.status === "quitado" ? "Realizado" : "Pendente"}</span>
                      <span className={`text-xs font-black ${t.kind === "pagar" ? "text-red-500" : "text-seed-salad"}`}>{t.kind === "pagar" ? "−" : "+"}{brl(t.amount_value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-5 text-xs font-semibold text-seed-deep/60">
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm bg-red-400" /> A pagar</span>
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm bg-seed-salad" /> A receber</span>
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full ring-2 ring-seed-vivid" /> Hoje</span>
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm bg-seed-vivid/20 ring-1 ring-seed-vivid/60" /> Período pesquisado</span>
          </div>
        </div>
      ) : effSubtab === "anual" ? (
        <>
          <div className="flex items-center justify-between rounded-lg bg-white p-5 shadow-sm">
            <button onClick={() => setAnualYear((y) => y - 1)} data-testid="anual-prev" className="rounded-full border border-seed-deep/20 px-5 py-2 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad">
              ← {anualYear - 1}
            </button>
            <h2 className="font-heading text-xl font-black tracking-tight text-seed-deep" data-testid="anual-title">Resultado de {anualYear}</h2>
            <button onClick={() => setAnualYear((y) => y + 1)} data-testid="anual-next" className="rounded-full border border-seed-deep/20 px-5 py-2 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad">
              {anualYear + 1} →
            </button>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="anual-cards">
            <div className="rounded-lg bg-white p-6 shadow-sm" data-testid="anual-entradas">
              <ArrowUpCircle size={20} className="text-seed-salad" />
              <p className="mt-3 font-heading text-2xl font-black text-seed-salad">{brl(anualTotals.receber)}</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-wider text-seed-deep/50">Entradas no ano</p>
            </div>
            <div className="rounded-lg bg-white p-6 shadow-sm" data-testid="anual-saidas">
              <ArrowDownCircle size={20} className="text-red-400" />
              <p className="mt-3 font-heading text-2xl font-black text-red-500">{brl(anualTotals.pagar)}</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-wider text-seed-deep/50">Saídas no ano</p>
            </div>
            <div className="rounded-lg bg-seed-deep p-6 text-seed-off shadow-sm" data-testid="anual-saldo">
              <CircleDollarSign size={20} className="text-seed-vivid" />
              <p className={`mt-3 font-heading text-2xl font-black ${anualTotals.receber - anualTotals.pagar >= 0 ? "text-seed-vivid" : "text-red-300"}`}>
                {brl(anualTotals.receber - anualTotals.pagar)}
              </p>
              <p className="mt-1 text-xs font-bold uppercase tracking-wider text-seed-off/60">Saldo previsto no ano</p>
            </div>
          </div>
          <div className="mt-6 rounded-lg bg-white p-6 shadow-sm" data-testid="anual-chart">
            <h3 className="mb-4 font-heading text-sm font-bold text-seed-deep">Entradas × saídas por mês (por vencimento)</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={anualData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#004C3F10" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => brl(v)} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="A receber" fill="#1AA886" radius={[4, 4, 0, 0]} />
                <Bar dataKey="A pagar" fill="#E11D48" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : effSubtab === "cadastros" ? (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <input
              placeholder="Buscar em todos os cadastros..."
              value={cadSearch}
              onChange={(e) => setCadSearch(e.target.value)}
              className="h-11 w-full max-w-md rounded-full bg-white px-5 text-sm font-semibold text-seed-deep shadow-sm outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]"
              data-testid="cad-search"
            />
            {cadSearch.trim() && (
              <button onClick={() => setCadSearch("")} className="flex items-center gap-1.5 rounded-full border border-seed-deep/20 px-4 py-2 text-xs font-bold text-seed-deep/60 transition-colors hover:text-red-500" data-testid="cad-search-clear">
                <X size={12} /> Limpar
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="cadastros-section">
          {!cadIsHidden(suppliersList) && (
          <CadastroPanel
            title="Fornecedores e clientes"
            testId="cad-suppliers"
            items={cadFilterItems(suppliersList)}
            open={cadIsOpen("cad-suppliers", suppliersList)}
            onToggle={() => toggleCad("cad-suppliers")}
            renderChips={(it) => (
              <>
                {[...it.kinds].map((k) => (
                  <span key={k} className={`rounded-full px-3 py-1 text-[10px] font-bold ${k === "pagar" ? "bg-red-50 text-red-500" : "bg-seed-salad/15 text-seed-salad"}`}>
                    {k === "pagar" ? "A pagar" : "A receber"}
                  </span>
                ))}
                {it.monthPagar > 0 && (
                  <span className="rounded-full bg-red-500/10 px-3 py-1 text-[10px] font-bold text-red-600" data-testid={`cad-suppliers-mes-${normTxt(it.name).replace(/\W+/g, "-").slice(0, 24)}`}>
                    Gasto no mês: {brl(it.monthPagar)}
                  </span>
                )}
                {it.monthReceber > 0 && (
                  <span className="rounded-full bg-seed-salad/15 px-3 py-1 text-[10px] font-bold text-seed-salad" data-testid={`cad-suppliers-mes-rec-${normTxt(it.name).replace(/\W+/g, "-").slice(0, 24)}`}>
                    Recebido no mês: {brl(it.monthReceber)}
                  </span>
                )}
              </>
            )}
            editName={cadEditing?.field === "supplier" ? cadEditing.name : null}
            input={cadInput}
            saving={cadSaving}
            onEdit={(name) => { setCadEditing({ field: "supplier", name }); setCadInput(name); }}
            onChange={setCadInput}
            onCancel={() => setCadEditing(null)}
            onSave={() => saveCadastro("supplier")}
            onDelete={(name) => deleteCadastro("supplier", name)}
          />
          )}
          {!cadIsHidden(centersList) && (
          <CadastroPanel
            title="Centros de custo"
            testId="cad-centers"
            items={cadFilterItems(centersList)}
            open={cadIsOpen("cad-centers", centersList)}
            onToggle={() => toggleCad("cad-centers")}
            renderChips={(it) =>
              it.budget ? (
                <span className="rounded-full bg-seed-vivid/20 px-3 py-1 text-[10px] font-bold text-seed-deep">Meta: {brl(Number(it.budget) || 0)}</span>
              ) : null
            }
            editName={cadEditing?.field === "cost_center" ? cadEditing.name : null}
            input={cadInput}
            saving={cadSaving}
            onEdit={(name) => { setCadEditing({ field: "cost_center", name }); setCadInput(name); }}
            onChange={setCadInput}
            onCancel={() => setCadEditing(null)}
            onSave={() => saveCadastro("cost_center")}
            onDelete={(name) => deleteCadastro("cost_center", name)}
          />
          )}
          {!cadIsHidden(tagsList) && (
          <CadastroPanel
            title="Tags"
            testId="cad-tags"
            items={cadFilterItems(tagsList)}
            open={cadIsOpen("cad-tags", tagsList)}
            onToggle={() => toggleCad("cad-tags")}
            editName={cadEditing?.field === "tags" ? cadEditing.name : null}
            input={cadInput}
            saving={cadSaving}
            onEdit={(name) => { setCadEditing({ field: "tags", name }); setCadInput(name); }}
            onChange={setCadInput}
            onCancel={() => setCadEditing(null)}
            onSave={() => saveCadastro("tags")}
            onDelete={(name) => deleteCadastro("tags", name)}
          />
          )}
          {!cadIsHidden(categoriesList) && (
          <CadastroPanel
            title="Categorias"
            testId="cad-categories"
            items={cadFilterItems(categoriesList)}
            open={cadIsOpen("cad-categories", categoriesList)}
            onToggle={() => toggleCad("cad-categories")}
            renderChips={(it) =>
              [...it.kinds].map((k) => (
                <span key={k} className={`rounded-full px-3 py-1 text-[10px] font-bold ${k === "pagar" ? "bg-red-50 text-red-500" : "bg-seed-salad/15 text-seed-salad"}`}>
                  {k === "pagar" ? "A pagar" : "A receber"}
                </span>
              ))
            }
            editName={cadEditing?.field === "category" ? cadEditing.name : null}
            input={cadInput}
            saving={cadSaving}
            onEdit={(name) => { setCadEditing({ field: "category", name }); setCadInput(name); }}
            onChange={setCadInput}
            onCancel={() => setCadEditing(null)}
            onSave={() => saveCadastro("category")}
            onDelete={(name) => deleteCadastro("category", name)}
          />
          )}
          </div>
        </>
      ) : (
        <>
          <div className="rounded-lg bg-white p-8 shadow-sm" data-testid="custos-section">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-seed-deep">
                <Home size={18} className="text-seed-salad" /> Custos por imóvel
              </h2>
              {costPropId && (
                <button
                  onClick={downloadPdf}
                  disabled={downloading}
                  data-testid="custos-pdf"
                  className="flex items-center gap-2 rounded-full bg-seed-vivid px-6 py-3 font-heading text-sm font-bold text-seed-deep transition-transform duration-300 hover:scale-105 disabled:opacity-60"
                >
                  <FileDown size={16} /> {downloading ? "Gerando..." : "Baixar relatório PDF"}
                </button>
              )}
            </div>
            <select value={costPropId} onChange={(e) => setCostPropId(e.target.value)} className={`${inputCls} mt-5 max-w-xl`} data-testid="custos-select">
              <option value="">Selecione um imóvel...</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.title} · {p.location}</option>
              ))}
            </select>

            {costProp && (
              <div className="mt-8">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="custos-cards">
                  <div className="rounded-lg bg-seed-off p-6" data-testid="custos-aquisicao">
                    <p className="text-xs font-bold uppercase tracking-wider text-seed-deep/50">Preço de aquisição</p>
                    <p className="mt-2 font-heading text-2xl font-black text-seed-deep">{aquisicao ? brl(aquisicao) : "Não informado"}</p>
                    {!aquisicao && <p className="mt-1 text-xs text-seed-deep/40">Edite o imóvel na aba Imóveis para informar.</p>}
                  </div>
                  <div className="rounded-lg bg-seed-off p-6" data-testid="custos-despesas">
                    <p className="text-xs font-bold uppercase tracking-wider text-seed-deep/50">Despesas (reforma, impostos...)</p>
                    <p className="mt-2 font-heading text-2xl font-black text-seed-deep">{brl(totalDespesas)}</p>
                    <p className="mt-1 text-xs text-seed-deep/40">{costExpenses.length} lançamento(s) vinculado(s)</p>
                  </div>
                  <div className="rounded-lg bg-seed-deep p-6 text-seed-off" data-testid="custos-total">
                    <p className="text-xs font-bold uppercase tracking-wider text-seed-off/60">Total investido</p>
                    <p className="mt-2 font-heading text-2xl font-black">{brl(totalInvestido)}</p>
                    <p className="mt-1 text-xs text-seed-off/50">Aquisição + despesas</p>
                  </div>
                </div>

                {dealDoImovel && (
                  <div className="mt-4 rounded-lg border-2 border-seed-vivid bg-white p-6" data-testid="custos-venda">
                    <p className="text-xs font-bold uppercase tracking-widest text-seed-salad">Resultado da venda</p>
                    <div className="mt-3 flex flex-wrap gap-x-10 gap-y-2">
                      <span className="text-sm text-seed-deep/70">Venda: <strong className="font-heading text-seed-deep">{dealDoImovel.sale_value}</strong></span>
                      <span className="text-sm text-seed-deep/70">Custo total: <strong className="font-heading text-seed-deep">{brl(totalInvestido)}</strong></span>
                      <span className="text-sm text-seed-deep/70">
                        Lucro real:{" "}
                        <strong className={`font-heading text-lg ${lucroReal !== null && lucroReal >= 0 ? "text-seed-salad" : "text-red-500"}`} data-testid="custos-lucro">
                          {lucroReal !== null ? brl(lucroReal) : "—"}
                        </strong>
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-6 space-y-2" data-testid="custos-expense-list">
                  {costExpenses.length === 0 ? (
                    <p className="text-sm text-seed-deep/40">Nenhuma despesa vinculada. Lance contas a pagar vinculadas a este imóvel na aba "Contas a pagar/receber".</p>
                  ) : (
                    costExpenses.map((t) => (
                      <div key={t.id} className="flex items-center justify-between rounded-md bg-seed-off px-4 py-3 text-sm">
                        <span className="font-semibold text-seed-deep/80">{t.description} <span className="text-xs text-seed-deep/40">({t.category})</span></span>
                        <span className="font-black text-red-500">− {brl(t.amount_value)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {groupEdit && (() => {
        const gtx = groupEdit.tx;
        const groupTxs = groupEdit.series
          ? txs.filter((x) => x.id === groupEdit.key || x.recurrence_source_id === groupEdit.key)
          : txs.filter((x) => x.installment_group === groupEdit.key);
        const settledCount = groupTxs.filter((x) => x.status === "quitado").length;
        const gf = (k) => (e) => setGroupForm((f) => ({ ...f, [k]: e.target.value }));
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-seed-deep/70 p-4" data-testid="group-edit-modal">
            <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-8 shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-heading text-xl font-bold text-seed-deep">{groupEdit.series ? "Editar série recorrente" : "Editar sequência de parcelas"}</h2>
                  <p className="mt-1 text-xs text-seed-deep/50">
                    {gtx.description} · {groupTxs.length} lançamento(s){settledCount > 0 ? ` · ${settledCount} já realizada(s)` : ""}
                  </p>
                </div>
                <button onClick={() => setGroupEdit(null)} aria-label="Fechar" data-testid="ge-close" className="text-seed-deep/40 transition-colors hover:text-red-500">
                  <X size={18} />
                </button>
              </div>
              <p className="mt-3 rounded-md bg-seed-off px-4 py-2 text-xs font-semibold text-seed-deep/60">
                Preencha apenas o que quiser mudar — os demais campos ficam como estão.
              </p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">{groupEdit.series ? "Valor de cada lançamento" : "Valor de cada parcela"}</label>
                  <input inputMode="decimal" placeholder={`atual: ${brl(gtx.amount_value)}`} value={groupForm.amount} onChange={(e) => setGroupForm((f) => ({ ...f, amount: maskMoney(e.target.value) }))} className={`${inputCls} mt-1`} data-testid="ge-amount" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Categoria</label>
                  <select value={groupForm.category} onChange={gf("category")} className={`${inputCls} mt-1`} data-testid="ge-category">
                    <option value="">Manter atual ({gtx.category || "—"})</option>
                    {[...cats[gtx.kind]].sort((a, b) => a.localeCompare(b, "pt-BR")).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">{gtx.kind === "pagar" ? "Fornecedor" : "Cliente"}</label>
                  <input list="ge-supplier-list" placeholder={`atual: ${gtx.supplier || "—"}`} value={groupForm.supplier} onChange={gf("supplier")} className={`${inputCls} mt-1`} data-testid="ge-supplier" />
                  <datalist id="ge-supplier-list">
                    {knownSuppliers.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Centro de custo</label>
                  <input list="ge-center-list" placeholder={`atual: ${gtx.cost_center || "—"}`} value={groupForm.costCenter} onChange={gf("costCenter")} className={`${inputCls} mt-1`} data-testid="ge-cost-center" />
                  <datalist id="ge-center-list">
                    {knownCenters.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Conta</label>
                  <select value={groupForm.account} onChange={gf("account")} className={`${inputCls} mt-1`} data-testid="ge-account">
                    <option value="">Manter atual ({gtx.account || "—"})</option>
                    {accounts.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Tags (vírgula)</label>
                  <input placeholder={`atual: ${(gtx.tags || []).join(", ") || "—"}`} value={groupForm.tagsText} onChange={gf("tagsText")} className={`${inputCls} mt-1`} data-testid="ge-tags" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">Observações</label>
                  <input placeholder={`atual: ${gtx.notes || "—"}`} value={groupForm.notes} onChange={gf("notes")} className={`${inputCls} mt-1`} data-testid="ge-notes" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-seed-deep/50">{groupEdit.series ? "Nova data base da série" : "Nova data da próxima parcela pendente"}</label>
                  <input type="date" value={groupForm.nextDue} onChange={gf("nextDue")} className={`${inputCls} mt-1`} data-testid="ge-next-due" />
                  <p className="mt-1 text-[10px] font-semibold text-seed-deep/40">{groupEdit.series ? "A nova data vira a base da série — as próximas gerações automáticas seguem a partir dela" : `As demais pendentes são redistribuídas (${gtx.frequency === "semanal" ? "a cada 7 dias" : "mensalmente"})`}</p>
                </div>
                <label className="flex items-center gap-2 self-end pb-3 text-xs font-bold text-seed-deep/70">
                  <input type="checkbox" checked={groupForm.includeSettled} onChange={(e) => setGroupForm((f) => ({ ...f, includeSettled: e.target.checked }))} className="h-4 w-4 accent-[#00A86B]" data-testid="ge-include-settled" />
                  Aplicar também às parcelas já realizadas
                </label>
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={saveGroupEdit} disabled={groupSaving} data-testid="ge-save" className="flex-1 rounded-full bg-seed-deep px-6 py-3.5 font-heading text-sm font-bold text-seed-off transition-transform hover:scale-[1.02] disabled:opacity-60">
                  {groupSaving ? "Salvando..." : `Salvar em ${groupTxs.filter((x) => groupForm.includeSettled || x.status !== "quitado").length} ${groupEdit.series ? "lançamento(s)" : "parcela(s)"}`}
                </button>
                <button onClick={() => setGroupEdit(null)} data-testid="ge-cancel" className="rounded-full border border-seed-deep/20 px-6 py-3.5 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-red-400 hover:text-red-500">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </main>
  );
}
